// Capa HTTP del dominio "informes" (Módulo E: creación del informe de
// tasación). Tipos + funciones fetch. El estado vive en TanStack Query
// (`src/hooks/useInformes.ts`), no acá — mismo patrón que `lib/usuarios.ts`.

import { apiFetch, descargarArchivo, urlDelApi } from "./api";
import { REFERENCIAS_DE_MERCADO } from "./modulos";
import { admiteReferenciasDeMercado } from "./referencias";

/** Los siete estados del informe (`modelo-de-datos.md` §3.3). */
export type EstadoInforme =
  | "incompleto"
  | "creado"
  | "cotizacion_en_revision"
  | "cotizacion_enviada_cliente"
  | "en_proceso"
  | "en_espera_revision_final"
  | "finalizado";

/** Los cuatro select contiguos de responsables (RF-E1 §8.1). */
export type RolResponsable =
  | "encargado_terreno"
  | "ejecutivo"
  | "revisor_cotizacion"
  | "revisor_informe";

export type CategoriaDocumento = "legal" | "historico";

/**
 * Las pestañas del expediente que GUARDAN NOTAS. El backend declara TRECE
 * (`modelo-de-datos.md` §8); estas son las construidas, y son el espejo exacto
 * de `SeccionInforme::construidas()` — el mapa `informe.notas` viene indexado
 * por ellas.
 */
export type SeccionInforme =
  | "general"
  | "legal"
  | "historico"
  | "descripcion"
  | "suelos"
  | "maquinaria"
  | "instalaciones"
  | "construcciones"
  | "plantaciones"
  | "agua";

/**
 * Rótulos de las pestañas del expediente, en el orden en que se muestran.
 *
 * Las de contenido TÉCNICO —lo que sale de la visita a terreno— van después
 * de las tres de antecedentes, que son el expediente que se arma antes.
 *
 * Los rótulos son CORTOS donde se puede: esta barra comparte ancho con las dos
 * pestañas de mercado, y en los equipos de Consultek (viewport de ~800 px) ya
 * se lleva casi todo el cuerpo de la tarjeta.
 *
 * `titulo` es el nombre COMPLETO, y existe porque el corto no siempre alcanza
 * para saber de qué pestaña se habla: "Aguas" es el rótulo y "Descriptor de
 * aguas" es la pestaña. Lo usa el tooltip de la tarjeta, que es donde el nombre
 * entero cabe sin competir con el ancho de la barra — y el encabezado de la
 * pestaña activa, que es donde se lee junto con `descripcion`.
 *
 * `descripcion` dice de qué trata la pestaña, y vive ACÁ y no dentro de cada
 * pestaña: el encabezado es uno solo para las doce, así que con el texto
 * repartido en doce componentes la próxima pestaña saldría sin él.
 */
export const PESTANAS: {
  seccion: SeccionInforme;
  etiqueta: string;
  titulo: string;
  descripcion: string;
}[] = [
  {
    seccion: "general",
    etiqueta: "Antecedentes generales",
    titulo: "Antecedentes generales",
    descripcion:
      "Datos de identificación, responsables, ubicación, roles y contenidos del documento.",
  },
  {
    seccion: "legal",
    etiqueta: "Antecedentes legales",
    titulo: "Documentación legal",
    descripcion: "Títulos, inscripciones, hipotecas, gravámenes y prohibiciones de la propiedad.",
  },
  {
    seccion: "historico",
    etiqueta: "Antecedentes históricos",
    titulo: "Antecedentes históricos",
    descripcion: "Tasaciones anteriores y hechos relevantes del predio.",
  },
  {
    seccion: "descripcion",
    etiqueta: "Descripción de la propiedad",
    titulo: "Descripción de la propiedad",
    descripcion:
      "Deslindes de cada rol y encuadres del predio: límites, vías de acceso y entorno.",
  },
  {
    seccion: "suelos",
    etiqueta: "Descripción de suelos",
    titulo: "Descripción de suelos",
    descripcion:
      "Describe los suelos de la propiedad y adjunta el mosaico de clases de uso de suelo.",
  },
  // ENTRE SUELOS Y MAQUINARIA, que es donde `modelo-de-datos.md` §8 la pone:
  // suelos y aguas son los recursos naturales del predio y se leen juntos;
  // después vienen los activos construidos. El enum del backend declaraba este
  // caso más abajo con un comentario que atribuía esa posición a §8, y se
  // corrigió al construir la pestaña (13-09-2026).
  //
  // "Aguas" a secas, aunque la pestaña se llame "Descriptor de aguas": misma
  // regla que instalaciones, construcciones y plantaciones.
  {
    seccion: "agua",
    etiqueta: "Aguas",
    titulo: "Descriptor de aguas",
    descripcion:
      "Describe el agua disponible en el predio para riego y otras obras de aprovechamiento.",
  },
  {
    seccion: "maquinaria",
    etiqueta: "Maquinarias y equipos",
    titulo: "Maquinarias y equipos",
    descripcion:
      "Agrega la maquinaria y equipos presentes en la propiedad para su valorización.",
  },
  // "Instalaciones" a secas, aunque la pestaña se llame "Instalaciones y obras
  // complementarias": con ocho rótulos más los dos de mercado, el largo
  // completo empuja la barra a scrollear de entrada en los equipos de
  // Consultek (viewport de ~800 px). El título largo sigue adentro, en el
  // encabezado de la pestaña, que es donde hace falta.
  {
    seccion: "instalaciones",
    etiqueta: "Instalaciones",
    titulo: "Instalaciones y obras complementarias",
    descripcion:
      "Agrega las instalaciones y obras complementarias presentes en la propiedad (losas, redes, cierros, obras civiles) y su valorización.",
  },
  {
    seccion: "construcciones",
    etiqueta: "Construcciones",
    titulo: "Construcciones",
    descripcion: "Agrega las construcciones presentes en la propiedad y su valorización.",
  },
  // "Plantaciones" a secas, aunque la pestaña se llame "Descriptor de
  // plantaciones": misma regla que las dos de arriba. El título completo vive
  // en el encabezado de la pestaña, que es donde hace falta.
  {
    seccion: "plantaciones",
    etiqueta: "Plantaciones",
    titulo: "Descriptor de plantaciones",
    descripcion:
      "Agrega las plantaciones frutales o forestales presentes en la propiedad. El cuadro las agrupa por especie.",
  },
];

/**
 * Las tres pestañas que existen desde que el informe se crea.
 *
 * NO es lo mismo que "es una sección del expediente": la descripción también
 * lo es, pero se habilita recién cuando el cliente acepta. Esta lista es la que
 * decide si corresponde explicarle al usuario que el resto está por abrirse —
 * sobre la propia pestaña de descripción ese aviso no describiría nada.
 */
export function esPestanaInicial(pestana: PestanaDelInforme): boolean {
  return pestana === "general" || pestana === "legal" || pestana === "historico";
}

/**
 * Si este informe tiene una TASACIÓN EN CURSO, o sea si el cliente ya aceptó.
 *
 * Es el mismo subconjunto de estados que habilita las referencias de mercado, y
 * por eso delega en aquella en vez de repetir la lista: el backend usa
 * `Informe::ESTADOS_CON_TASACION` para las dos cosas, y dos copias de tres
 * estados se desincronizan en cuanto entre un estado nuevo.
 *
 * DOS NOMBRES PARA UN MISMO PREDICADO, a propósito: quien lee la pestaña
 * "Descripción de la propiedad" no está pensando en comparables, y llamar acá a
 * `admiteReferenciasDeMercado` mandaría a buscar un módulo que no tiene nada
 * que ver con lo que está mirando.
 */
export function tieneTasacionEnCurso(estado: string): boolean {
  return admiteReferenciasDeMercado(estado);
}

/**
 * Las pestañas de la ficha del informe, que son MÁS que las secciones del
 * expediente.
 *
 * `SeccionInforme` NO se extendió con estas dos, y no es un detalle: ese tipo
 * indexa `informes.notas` y la obligatoriedad de documentos del backend, así
 * que agregarle claves obligaría a inventar una nota y un estado "pendiente"
 * para dos pestañas que solo LEEN. Son otra naturaleza —no se guardan, no
 * tienen guardia de cambios— y por eso son otro tipo.
 */
export type PestanaDeMercado = "cbr" | "ofertas";

/**
 * El registro de terreno: lo que el encargado sincronizó desde la App. Solo
 * lectura como las de mercado, pero NO es mercado: no depende del módulo de
 * referencias y su fuente es otra (la App de terreno), así que tiene su propio
 * tipo y su propia banda en la barra.
 */
export type PestanaDeTerreno = "terreno";

export type PestanaDelInforme = SeccionInforme | PestanaDeMercado | PestanaDeTerreno;

/**
 * Si esta pestaña es una del expediente, que sí se guarda y tiene notas.
 *
 * SE DERIVA DE `PESTANAS` Y NO ENUMERA LOS CASOS A MANO, y eso corrige un
 * defecto: la lista escrita a mano se había quedado sin `"suelos"` —o sea que
 * este type guard devolvía `false` para una sección que sí lo es— y nadie se
 * enteró porque hoy no lo llama nadie. Derivado de la lista que ya existe, la
 * pestaña que entre mañana queda cubierta sin que haya que acordarse.
 *
 * Es exactamente lo que el comentario de `pestanasVisibles` ya decía sobre las
 * listas de excepciones: "la pestaña técnica que entre mañana se dibujaría
 * antes de tiempo por olvido". El olvido había ocurrido acá.
 */
export function esSeccionDelExpediente(pestana: PestanaDelInforme): pestana is SeccionInforme {
  return PESTANAS.some(({ seccion }) => seccion === pestana);
}

/* --------------------------------------------------------------------------
   La pestaña activa viaja en la URL (`/informes/7/suelos`).

   Antes vivía en un `useState` de la pantalla, y eso costaba tres cosas:
   recargar devolvía a "Antecedentes generales" —cada pestaña trae mucho
   trabajo cargado—, no se podían abrir dos pestañas del mismo informe en dos
   ventanas, y no había enlace que compartir.

   Es la MISMA decisión que el repo ya tomó dos veces: `CotizacionEnviada` dejó
   de ser estado local del formulario y pasó a ruta propia, y el origen de
   navegación se puso en la url porque "el estado del router se pierde al
   recargar, al compartir el enlace y al volver con el historial"
   (`origenDeNavegacion.ts`).

   Y de ahí sale la otra regla que se hereda de aquel archivo: lo que viene de
   la barra de direcciones es un valor que el usuario controla, así que se
   VALIDA contra un vocabulario cerrado y nunca se castea.
   -------------------------------------------------------------------------- */

/**
 * Las dos pestañas de mercado como VALORES, no solo como tipo.
 *
 * El guardián de abajo es lo que vuelve confiable a esta lista: si mañana
 * entra una tercera clase de evidencia en `PestanaDeMercado` y nadie la agrega
 * acá, `tsgo` se pone rojo. Sin él, esa pestaña dejaría de ser direccionable y
 * el síntoma sería mudo — la url caería a "Antecedentes generales" sin que
 * nada fallara.
 */
const MERCADO = ["cbr", "ofertas"] as const satisfies readonly PestanaDeMercado[];

type _MercadoSinRegistrar = Exclude<PestanaDeMercado, (typeof MERCADO)[number]>;
const _TODO_MERCADO_REGISTRADO: [_MercadoSinRegistrar] extends [never] ? true : never = true;

/** El registro de terreno como VALOR, con el mismo guardián que las de mercado. */
const TERRENO = ["terreno"] as const satisfies readonly PestanaDeTerreno[];

type _TerrenoSinRegistrar = Exclude<PestanaDeTerreno, (typeof TERRENO)[number]>;
const _TODO_TERRENO_REGISTRADO: [_TerrenoSinRegistrar] extends [never] ? true : never = true;

/**
 * La pestaña que se muestra cuando la url no trae segmento.
 *
 * VA ESCRITA Y NO SE DERIVA DE `PESTANAS[0]`: si se derivara, reordenar la
 * barra cambiaría en silencio qué pantalla abre `/informes/7`, que es la url
 * que ya emiten `retornoA("informe")`, el banner de cotización y las cuatro
 * pantallas desde las que se entra a editar.
 */
export const PESTANA_POR_DEFECTO: PestanaDelInforme = "general";

/**
 * El vocabulario CERRADO de pestañas que puede aparecer en la URL.
 *
 * Las secciones se DERIVAN de `PESTANAS` en vez de transcribirse: es la misma
 * regla con la que `esSeccionDelExpediente` dejó de enumerar casos a mano
 * —ahí el olvido ya había ocurrido con `"suelos"`— y hace que la pestaña que
 * entre mañana sea direccionable sin tocar este archivo.
 */
export const PESTANAS_DEL_INFORME: readonly PestanaDelInforme[] = [
  ...PESTANAS.map(({ seccion }) => seccion),
  ...MERCADO,
  ...TERRENO,
];

/**
 * Si este texto es una pestaña del informe.
 *
 * NO normaliza mayúsculas a propósito: la url canónica es una sola, y aceptar
 * "Suelos" dejaría dos direcciones para la misma pantalla.
 */
export function esPestanaDelInforme(valor: string): valor is PestanaDelInforme {
  return PESTANAS_DEL_INFORME.some((pestana) => pestana === valor);
}

/**
 * La pestaña que pide la url, o `null` si el segmento no es ninguna.
 *
 * SON TRES RESPUESTAS Y NO DOS, y esa es la razón de que devuelva `null` en
 * vez de caer a la pestaña por defecto: quien llama tiene que poder
 * distinguir "no vino segmento" —que se muestra tal cual, porque `/informes/7`
 * es una url legítima y no debe rebotar— de "vino algo que no existe", que
 * REDIRIGE. La barra de direcciones no puede decir una pestaña y mostrar otra.
 */
export function pestanaDesdeLaUrl(segmento: string | undefined): PestanaDelInforme | null {
  if (segmento === undefined) return PESTANA_POR_DEFECTO;

  return esPestanaDelInforme(segmento) ? segmento : null;
}

/**
 * La url de una pestaña, conservando el `search`.
 *
 * EL SEARCH NO ES OPCIONAL POR PROLIJIDAD: ahí vive `desde`, que decide el
 * enlace de "volver" y el camino de migas (`origenDeNavegacion.ts`).
 * Perderlo al cambiar de pestaña mandaría al revisor de vuelta al listado del
 * ejecutivo, donde nunca estuvo.
 *
 * LA PESTAÑA POR DEFECTO NO LLEVA SEGMENTO: `/informes/7` es su url canónica
 * y la que el resto del sistema ya emite. Escribir `/informes/7/general`
 * dejaría dos direcciones para la misma pantalla; a mano sigue funcionando,
 * pero no la genera nadie.
 */
export function rutaDePestana(
  informeId: number,
  pestana: PestanaDelInforme,
  search = "",
): string {
  const base =
    pestana === PESTANA_POR_DEFECTO ? `/informes/${informeId}` : `/informes/${informeId}/${pestana}`;

  const consulta = search.replace(/^\?/, "");

  return consulta === "" ? base : `${base}?${consulta}`;
}

/**
 * Las pestañas que este informe puede mostrar HOY, en el orden de
 * `PESTANAS_DEL_INFORME`.
 *
 * ÚNICA FUENTE para "qué pestañas existen". Antes `Contenido` calculaba
 * `conTasacion` y `conReferencias` por su cuenta para dibujar la barra; el
 * redirect de una url inválida (`pestanaDesdeLaUrl` devolviendo `null`, o una
 * pestaña que existe pero todavía no está habilitada) necesita la MISMA
 * respuesta, y antes de que `Contenido` llegue a montarse. Dos cuentas
 * separadas es exactamente cómo la barra y el redirect terminarían
 * discrepando: la url rebotaría una pestaña que la barra sí dibuja, o al
 * revés.
 */
export function pestanasDisponibles(estado: EstadoInforme): PestanaDelInforme[] {
  const conTasacion = tieneTasacionEnCurso(estado);
  // Las de mercado no dependen SOLO del estado: el módulo entero se puede
  // apagar en el despliegue (`lib/modulos.ts`), y con él apagado sus rutas
  // caen en el 404 — dibujarlas sería una puerta a una pantalla que no existe.
  const conReferencias = REFERENCIAS_DE_MERCADO && admiteReferenciasDeMercado(estado);

  return PESTANAS_DEL_INFORME.filter((pestana) => {
    // El registro de terreno NO cuelga del módulo de mercado: lo escribe la App
    // de terreno. Existe desde la aceptación, igual que las técnicas.
    if (pestana === "terreno") return conTasacion;
    if (!esSeccionDelExpediente(pestana)) return conReferencias;

    // Las técnicas —descripción de la propiedad, suelos, maquinarias…—
    // comparten el motivo de las de mercado: el backend responde 422 a
    // cualquier estado anterior a la aceptación (`Informe::ESTADOS_CON_TASACION`),
    // así que dibujarlas antes sería una pestaña que solo sabe mostrar un
    // error.
    return esPestanaInicial(pestana) || conTasacion;
  });
}

/**
 * Si el DESTINO de una navegación bloqueada es otra pestaña de ESTE MISMO
 * informe, para que la guardia de cambios sin guardar distinga "cambias de
 * pestaña" de "sales de la pantalla" —incluso cuando la salida la dispara el
 * router y no un clic, como el botón "atrás" entre dos pestañas.
 *
 * NO ALCANZA CON MIRAR EL PREFIJO: `/informes/7/cotizacion` empieza igual que
 * `/informes/7/suelos`, pero es OTRA pantalla —no una pestaña del editor—, así
 * que confundirlas le pondría a "Cotizar informe" el aviso de "cambias de
 * pestaña", que describe otra cosa. Por eso el segmento que sigue a
 * `/informes/{id}/` se valida contra el vocabulario CERRADO de
 * `esPestanaDelInforme`, y no contra cualquier segmento no vacío.
 *
 * `destino` toma solo `{ pathname }` y no el `Location` completo de
 * react-router: es un predicado del DOMINIO, y no tiene por qué conocer ese
 * tipo — un `Location` real lo satisface igual, por estructura.
 */
export function esCambioDePestanaDelMismoInforme(
  informeId: number,
  destino: { pathname: string },
): boolean {
  const prefijo = `/informes/${informeId}`;

  // Sin segmento es la pestaña por defecto: `/informes/7` es tan pestaña como
  // `/informes/7/suelos`.
  if (destino.pathname === prefijo) return true;

  if (!destino.pathname.startsWith(`${prefijo}/`)) return false;

  const segmento = destino.pathname.slice(prefijo.length + 1);

  // Un segmento con MÁS partes (`cotizacion/enviada`) no es una pestaña: las
  // pestañas son siempre el único segmento que sigue al id.
  return !segmento.includes("/") && esPestanaDelInforme(segmento);
}

/**
 * TRES valores, y "Sin definir" ya no existe: un documento sin elección es
 * Obligatorio (decisión de producto del 29-09-2026, que supera a RF-E5). El
 * servidor entrega `Informe.documentos` ya resuelto —con el Obligatorio por
 * defecto puesto—, y el mapa de la pantalla usa `Obligatoriedad | undefined`
 * donde `undefined` se lee como Obligatorio.
 *
 * "no_aplica" SÍ es una elección: alguien decidió que el documento no
 * corresponde a esta propiedad, y no se le pide al cliente.
 */
export type Obligatoriedad = "obligatorio" | "opcional" | "no_aplica";

/* --------------------------------------------------------------------------
   Cotización (Módulo F, RF-F1/F2/F3)
   -------------------------------------------------------------------------- */

export type ModoGeneracionCotizacion = "manual" | "ia";

export type EstadoCotizacion =
  | "borrador"
  | "en_revision"
  | "enviada_cliente"
  | "aceptada"
  | "anulada";

/** Espejo de `App\Enums\OrigenAceptacion`. */
export type OrigenAceptacion = "manual" | "enlace_publico";

export interface Cotizacion {
  id: number;
  informe_id: number;
  version: number;
  /** String por el cast `decimal:2`: es dinero, se formatea y no se recalcula. */
  precio_uf: string;
  /**
   * El cuadro de honorarios. Lo usa el formulario para que RECOTIZAR arranque
   * donde quedó la versión anterior: sin esto, corregir un precio obligaría a
   * volver a escribir todas las líneas.
   *
   * Opcional en el tipo por las cotizaciones anteriores a la tabla, que no
   * tienen ninguna.
   */
  lineas?: CotizacionLinea[];
  /**
   * Los ítems de Metodología y Documentación elegidos, por id. Viaja la
   * ELECCIÓN y no el texto: el texto ya está congelado en el PDF.
   */
  items?: number[];
  /** Los documentos pedidos, por id del tipo. Para precargar al recotizar. */
  documentos?: number[];
  /**
   * Lo que se PACTA: el plazo de entrega en días hábiles.
   *
   * `null` solo en las cotizaciones anteriores al cambio de modelo, donde se
   * acordó una fecha y no un plazo.
   */
  plazo_dias_habiles: number | null;
  /**
   * ¿Esta cotización pide abono? Es una condición COMERCIAL: se le ofrece al
   * cliente y se imprime en el documento.
   *
   * TRES VALORES, no dos. `null` es "no informado" —las cotizaciones
   * anteriores a la columna— y no "no requiere": mostrarles "No requiere
   * abono" les atribuiría una condición que nadie pactó. Ver `describirAbono`.
   */
  requiere_abono: boolean | null;
  /** String por el cast `decimal:2`. Solo tiene valor cuando hay abono. */
  porcentaje_abono: string | null;
  /**
   * El día en que el cliente PAGÓ el abono (ISO `yyyy-mm-dd`).
   *
   * OTRA COSA que las dos de arriba, y confundirlas es el error a evitar:
   * aquellas son la condición que se le OFRECE al cliente y viajan impresas en
   * el documento; esta es el hecho de que la plata entró, se registra después
   * de enviada la cotización y no sale en ningún papel.
   *
   * `null` es "todavía no pagado". Que la cotización no pida abono es un
   * estado DISTINTO y se lee en `requiere_abono`. Ver `estadoDelAbono`.
   */
  abono_pagado_en: string | null;
  /** Cuándo se dejó el registro, que no es el día del pago. */
  abono_registrado_en?: string | null;
  /**
   * Quién firmó el registro.
   *
   * Ausente cuando el endpoint no cargó la relación —solo la trae el listado y
   * la respuesta del propio registro—, y `null` cuando se dio de baja al
   * usuario que lo registró sin borrar el hecho.
   */
  abono_registrado_por?: { id: number; nombre: string } | null;
  /**
   * Un día del calendario comprometido — SOLO en las cotizaciones aceptadas
   * bajo el modelo anterior, que lo derivaba de la aceptación.
   *
   * LA APLICACIÓN YA NO LO ESCRIBE. El plazo corre a contar de la visita de
   * inspección y esa visita no se registra todavía, así que no hay fecha que
   * derivar; `null` es lo normal y no un dato faltante. Se conserva porque
   * esas cotizaciones viejas ya le comunicaron esa fecha al cliente por
   * escrito. Ver `describirEntrega`, que la prefiere cuando está.
   */
  fecha_entrega: string | null;
  /**
   * La agenda INTERNA del equipo (ISO `yyyy-mm-dd`), no un compromiso.
   *
   * No salen en el PDF del cliente y no reemplazan a nada:
   * `plazo_dias_habiles` es lo que se pacta —y corre a contar de la visita de
   * inspección— y `fecha_entrega_estimada` lo que el equipo planifica al
   * cotizar. Que no coincidan es el punto, no un error.
   *
   * `null` es "todavía no se agendó", que al cotizar es lo habitual.
   */
  fecha_visita_estimada: string | null;
  fecha_entrega_estimada: string | null;
  modo_generacion: ModoGeneracionCotizacion;
  estado: EstadoCotizacion;
  generada_en: string | null;
  /** Cuándo el revisor la aprobó (RF-G3). `null` mientras espera el visado. */
  enviada_en: string | null;
  /** Cuándo aceptó el cliente. `null` mientras no lo hace. */
  aceptada_en?: string | null;
  /**
   * Por dónde entró la aceptación.
   *
   * No es una preferencia de registro: `manual` es un hecho que transcribió un
   * empleado —y por eso lleva motivo— y `enlace_publico` es uno que ejecutó el
   * cliente por su cuenta.
   */
  origen_aceptacion?: OrigenAceptacion | null;
  /** El respaldo del registro manual ("aceptó por teléfono el 27-08"). */
  motivo_aceptacion?: string | null;
  /** Solo en la respuesta del alta: a quiénes va el correo de visado. */
  revisores?: { id: number; nombre: string; email: string }[];
  /** Solo en la respuesta del alta: el estado al que quedó el informe. */
  informe_estado?: EstadoInforme;
}

/** Una línea del cuadro de honorarios tal como la devuelve el API. */
export interface CotizacionLinea {
  id: number;
  tipo: "tasacion" | "gastos" | "descuento";
  concepto: string;
  /** String por el cast `decimal:2`: es dinero y el front lo formatea. */
  monto_uf: string;
  /** Solo un descuento pactado en porcentaje; `null` en el resto. */
  porcentaje?: string | null;
  orden: number;
}

export interface CrearCotizacionInput {
  /**
   * El TOTAL del cuadro, derivado de las líneas y no tipeado aparte. Sigue
   * viajando porque es el precio de la cotización, pero ya no es un dato de
   * entrada: tenerlo como campo propio era un segundo lugar donde vivía la
   * misma cifra.
   */
  precio_uf: number;
  /**
   * El cuadro que imprime el documento. De acá sale también la cantidad de
   * informes comprometidos: es el conteo de líneas de tipo `tasacion`.
   */
  lineas: {
    tipo: "tasacion" | "gastos" | "descuento";
    concepto: string;
    /** SIEMPRE positivo, incluso el descuento: el signo lo pone el tipo. */
    monto_uf: number;
    /** Solo un descuento en porcentaje: el servidor deriva el monto de él. */
    porcentaje?: number;
    orden: number;
  }[];
  /**
   * Los ítems de Metodología elegidos, EN EL ORDEN en que se imprimen. Viajan
   * los ids: el texto lo resuelve el backend contra el catálogo y queda
   * congelado en el PDF.
   *
   * HOY NO LO MANDA NADIE, y por eso es opcional: la metodología dejó de
   * elegirse en la pantalla (01-09-2026) y el backend arma la estándar cuando
   * la clave no viene. Mandar `[]` NO es lo mismo que omitirla: sería pedir un
   * apartado vacío. El campo sigue en el contrato porque el API lo acepta y la
   * elección vuelve cuando se defina el catálogo por tipo de activo.
   */
  items?: number[];
  /**
   * Los DOCUMENTOS que el presupuesto pide, por id del tipo. Salen del maestro
   * del informe: es la misma lista que se marcó como Documentación Legal.
   */
  documentos: number[];
  plazo_dias_habiles: number;
  modo_generacion: ModoGeneracionCotizacion;
  /**
   * Siempre viaja, incluso en `false`: el API lo exige. Omitirlo dejaría que
   * un front desactualizado guardara "no requiere abono" sin que nadie lo
   * decidiera, en un documento que el cliente acepta.
   */
  requiere_abono: boolean;
  /** Solo cuando hay abono; `null` cuando el interruptor está apagado. */
  porcentaje_abono: number | null;
  /** ISO `yyyy-mm-dd`, o `null` si todavía no se agendó. Datos internos. */
  fecha_visita_estimada: string | null;
  fecha_entrega_estimada: string | null;
}

/**
 * Rótulos de los cuatro select, en el orden en que se muestran.
 *
 * El orden del arreglo ES el orden visual: el formulario los recorre dentro de
 * una grilla de dos columnas, así que se leen de izquierda a derecha y de
 * arriba abajo. El ejecutivo va PRIMERO porque es quien crea el informe —
 * abrir el bloque por el responsable que ya está frente a la pantalla y recién
 * después pedir a los demás sigue el orden en que la persona los tiene en
 * mente. Los tres restantes van en el orden del flujo de trabajo: terreno,
 * revisión de la cotización y revisión del informe final.
 */
export const ROLES_RESPONSABLE: { rol: RolResponsable; etiqueta: string; placeholder: string }[] = [
  {
    rol: "ejecutivo",
    etiqueta: "Ejecutivo a cargo de revisar y llenar el informe",
    placeholder: "Selecciona un ejecutivo",
  },
  { rol: "encargado_terreno", etiqueta: "Encargado de terreno", placeholder: "Selecciona un usuario" },
  { rol: "revisor_cotizacion", etiqueta: "Revisor de la cotización", placeholder: "Selecciona un revisor" },
  { rol: "revisor_informe", etiqueta: "Revisor del informe final", placeholder: "Selecciona un revisor" },
];

/**
 * Una plantilla que se puede elegir al crear un informe. El detalle de sus
 * ítems no viaja acá: se pide al elegirla (`useContenidosDeLaPlantilla`).
 */
export interface PlantillaDelCatalogo {
  id: number;
  nombre: string;
  proposito: string | null;
  /** Los formatos de salida que admite, con el que se propone por defecto. */
  formatos: FormatoDelCatalogo[];
}

export interface FormatoDelCatalogo {
  id: number;
  nombre: string;
  por_defecto: boolean;
}

/**
 * Un contenido del informe tal como quedó CONGELADO al guardarlo: nombre,
 * ubicación y obligatoriedad de ese día, aunque la plantilla o el maestro
 * cambien después (mismo trato que todo lo que entra a un documento que se
 * firma).
 */
export interface ItemDelInforme {
  maestro_item_id: number;
  nombre: string;
  grupo: string;
  subgrupo: string | null;
  orden: number;
  obligatoriedad: "obligatorio" | "condicional" | "opcional";
  condicion: string | null;
}

export interface DocumentoDelCatalogo {
  id: number;
  /**
   * Identidad ESTABLE de los documentos que siembra el sistema. `null` en los
   * que dio de alta un usuario.
   *
   * El importador de tasaciones lo usa para saber a qué documento adjuntar el
   * PDF: el `nombre` es editable desde el mantenedor, así que identificarlos por
   * nombre haría que renombrar uno dejara el importador adjuntando al
   * equivocado, sin que nada fallara.
   */
  codigo: string | null;
  nombre: string;
  categoria: CategoriaDocumento;
  orden: number;
}

/** Los dos documentos entre los que se elige al importar una tasación anterior. */
export const CODIGO_TASACION_CONSULTEK = "tasaciones-anteriores-consultek";
export const CODIGO_TASACION_CLIENTE = "tasaciones-anteriores-cliente";

/** El documento `legal` al que se adjunta el certificado de avalúo fiscal del SII. */
export const CODIGO_CERTIFICADO_AVALUO_SII = "certificado-avaluo-sii";

/**
 * Un archivo que se RECIBIÓ para un tipo de documento del informe: la tasación
 * anterior, el plano, el certificado.
 *
 * No confundir con `Informe["documentos"]`, que dice qué se le EXIGE al cliente.
 * Son dos hechos independientes: puede haber tres tasaciones adjuntas sin que
 * nadie haya decidido todavía si eran obligatorias.
 *
 * Nunca trae la ruta del bucket: los bytes se piden por
 * `urlDeArchivoDeAntecedente()`, que apunta al endpoint autenticado.
 */
export interface ArchivoDeAntecedente {
  id: number;
  /**
   * El tipo de documento al que el equipo clasificó el archivo. `null` es "sin
   * clasificar": el cliente lo mandó por el portal sin saber qué documento era
   * y nadie lo ha ordenado todavía. Solo puede pasarle a un archivo del cliente.
   */
  documento_tipo_id: number | null;
  /**
   * El espacio del portal por el que llegó (`legal`, `tasaciones`,
   * `descriptiva`), o `null` si lo cargó el equipo. NO cambia al clasificar.
   */
  seccion_cliente: "legal" | "tasaciones" | "descriptiva" | null;
  /** El nombre que traía en la máquina de quien lo subió. */
  nombre_original: string;
  archivo_bytes: number;
  archivo_mime: string;
  subido_en: string | null;
  /**
   * Nombre de quien lo subió, ya armado por el backend. `null` cuando lo mandó
   * el cliente: no tiene cuenta en Consultek.
   */
  subido_por: string | null;
  /**
   * Quién lo dejó. Desde que existe el portal ya NO se deduce de `subido_por`:
   * el cliente sube sin cuenta, así que ese campo viene `null` y `null` solo no
   * distingue "lo mandó el cliente" de "se perdió la firma".
   */
  origen: "equipo" | "cliente" | null;
  /**
   * En qué quedó su versión en Markdown, la que lee la IA.
   *
   * `null` ES UN CASO REAL y significa "todavía nadie lo intentó": o el trabajo
   * está en la cola, o el archivo se subió antes de que esta función existiera.
   * No es lo mismo que `listo`, y la pantalla los dice distinto.
   */
  contexto_estado: EstadoDelContexto | null;
  /**
   * Por qué no se pudo, cuando el estado es `fallido`. Es un CÓDIGO, no una
   * frase: qué hacer con cada uno lo traduce `contextoDelArchivo.ts`.
   */
  contexto_motivo: string | null;
  /**
   * Si el procesamiento del contexto está EN VUELO — lo decide el SERVIDOR
   * (`InformeDocumentoArchivo::contextoEnCurso()`), nunca esta pantalla: la
   * ventana entre "recién despachado" y "se trabó" depende de los reintentos y
   * el timeout del worker, que acá no se conocen. `true` mientras un trabajo
   * puede estar corriendo de verdad; sondear la ficha mientras esto sea
   * `true` es lo que la mantiene al día sin reintentos manuales.
   */
  contexto_en_curso: boolean;
  /**
   * Si corresponde ofrecer "Reintentar". Mismo punto único que
   * `contexto_en_curso`, del lado del servidor: un `fallido` siempre lo es, y
   * un `null`/`necesita_ocr` lo es solo cuando dejó de estar en curso —o sea
   * trabado, no procesándose.
   */
  contexto_reintentable: boolean;
  /**
   * Cuándo se EMITIÓ el documento —no cuándo se subió, eso ya lo dice
   * `subido_en`—, ISO `yyyy-mm-dd` o `null` si nadie la cargó todavía. Rebanada
   * 2 de `odd/tasks/fecha-del-documento-y-tasacion-mas-reciente.md`: decide qué
   * tasación anterior gana cuando hay varias del mismo predio.
   */
  documento_fecha: string | null;
  /**
   * Quién la puso: la propuso la IA (`ia`, y conviene revisarla) o la corrigió
   * una persona (`manual`). `null` cuando `documento_fecha` también es `null`.
   */
  documento_fecha_origen: "ia" | "manual" | null;
  /**
   * La nota del tasador sobre ESTE archivo (`odd/tasks/notas-por-archivo.md`):
   * lo que él sabe del documento y el documento no dice. La lee la IA al
   * rellenar y NUNCA sale por el portal del cliente. `null` si nadie anotó.
   */
  nota: string | null;
  /**
   * Si a ESTE tipo de documento le corresponde tener fecha de emisión — lo
   * decide el BACKEND (`CampoDelRelleno::GRUPO_DE_TASACIONES`), nunca este
   * archivo: si la lista de códigos viviera acá, sería la misma regla escrita
   * en dos lugares, y un tipo nuevo de tasación se quedaría sin el campo en
   * silencio.
   */
  fecha_aplica: boolean;
}

/**
 * En qué quedó el intento de producir la versión en Markdown de un archivo.
 *
 * Espejo de `App\Enums\EstadoDelContexto`. LOS TRES CASOS NO SON "salió bien" y
 * "salió mal": `necesita_ocr` es un archivo perfectamente legible que la
 * conversión no puede leer —un escaneo, una fotografía— y del que se ocupa la
 * transcripción con IA. La ausencia de estado (`null`) es el cuarto caso y vive
 * en el campo, no acá.
 */
export type EstadoDelContexto = "listo" | "necesita_ocr" | "fallido";

/** Los datos con los que se le factura al cliente de este informe. */
export interface FacturacionDelInforme {
  rut: string | null;
  razon_social: string | null;
  giro: string | null;
  direccion: string | null;
  /** Resuelto contra el maestro; el informe no guarda copia congelada. */
  comuna: string | null;
  email_facturacion: string | null;
  telefono: string | null;
  contacto_nombre: string | null;
  origen: "equipo" | "cliente" | null;
  actualizado_en: string | null;
}

/**
 * Persona asignable a un rol del informe. Trae `perfil_codigo` —el CÓDIGO, no
 * el nombre, porque el nombre del perfil es editable (RF-C3)— para que la
 * pantalla arme el select de revisores sin una segunda llamada.
 */
export interface UsuarioAsignable {
  id: number;
  nombre: string;
  perfil_codigo: string | null;
}

export interface CatalogosDelInforme {
  plantillas: PlantillaDelCatalogo[];
  documentos: Record<CategoriaDocumento, DocumentoDelCatalogo[]>;
  usuarios: UsuarioAsignable[];
}

/**
 * Un rol del informe: la fila de la tabla "Detalle inmuebles tasación".
 *
 * REVERSIÓN DELIBERADA de un árbol de tres niveles (bloque → propiedad → rol)
 * reconstruido de cinco cotizaciones reales que compartían un defecto de raíz:
 * ninguna estaba estandarizada, cada ejecutivo acomodaba el documento a su
 * gusto. La 22160 eran en realidad CINCO tasaciones distintas metidas en un
 * solo documento —el caso incorrecto que motivó el nivel "propiedad"—; la
 * 22192 (una propiedad, varios roles, una comuna) es el caso correcto, y lo
 * único que lo extiende es la situación real de una sucesión cuyos roles caen
 * en comunas DISTINTAS. Por eso la comuna y la superficie viven en el ROL y no
 * en un nivel intermedio: "rol + comuna es la unidad que el SII reconoce"
 * (`api/database/migrations/2026_08_25_110100_create_informe_roles_table.php:13`),
 * y dos roles de una misma comuna nunca comparten número.
 */
export interface RolDeInforme {
  id: number;
  rol_sii: string;
  /**
   * El nombre de la comuna CONGELADO en el informe: es lo que el informe dice
   * y lo que imprime el PDF, aunque el maestro la renombre después. `null` en
   * un informe incompleto o mientras nadie eligió comuna.
   */
  comuna: string | null;
  /**
   * El código CUT de la comuna elegida del registro oficial. `null` en los
   * informes anteriores al maestro, que tienen el nombre escrito a mano.
   */
  comuna_codigo: string | null;
  /**
   * Strings por el cast `decimal:2` del backend: se suman con precisión
   * decimal.
   *
   * `null` NO significa lo mismo en las dos: terreno vacío se imprime "—" (no
   * corresponde; el suelo del departamento es Bien Común) y construida vacía
   * "s/i" (existe y nadie la midió).
   */
  superficie: string | null;
  superficie_construida: string | null;
  orden: number;

  /* --- Pestaña "Descripción de la propiedad" --- */

  /**
   * Con qué limita el rol por cada punto cardinal ("Norte: con camino público
   * vecinal…"). Prosa con asistencia de IA; `null` es lo normal hasta después
   * de la visita a terreno.
   *
   * Opcional en el tipo porque el LISTADO de informes no carga esta pestaña.
   */
  descripcion_deslindes?: string | null;
  /**
   * "Yo revisé esto". Va POR ROL y no por pestaña: con nueve roles, una
   * casilla al pie no diría cuál de las nueve descripciones se revisó — que es
   * justamente lo que la casilla existe para garantizar cuando el texto lo
   * propuso la IA.
   */
  descripcion_deslindes_validada?: boolean;
  /**
   * La captura del KMZ con el límite de este rol, o `null` si todavía no
   * tiene. NUNCA la ruta del bucket: los bytes se piden a
   * `urlDeLaImagenDelRol()`, que apunta al endpoint autenticado.
   */
  imagen?: ImagenDeInforme | null;

  /* --- Antecedentes legales (tabla legal de roles) ---
     Se editan en "Descripción de la propiedad". Las superficies llegan como
     strings `decimal:2` y van en la unidad del informe; `null` es «sin cargar»
     (se imprime «—», y en la inscripción «s/i»). La superficie de planimetría
     se mide: la IA no la propone. Opcionales en el tipo porque el LISTADO no
     las carga. */
  nombre_predio?: string | null;
  superficie_titulos?: string | null;
  superficie_sii?: string | null;
  superficie_planimetria?: string | null;
  destino_sii?: string | null;
  avaluo_fiscal_clp?: number | null;
  inscripcion_fojas?: string | null;
  inscripcion_numero?: string | null;
  inscripcion_anio?: number | null;
  inscripcion_cbr?: string | null;
}

/**
 * Los encuadres del expediente que se piden UNA SOLA VEZ por informe.
 *
 * Tres son de "Descripción de la propiedad" —el polígono completo, el acceso
 * desde las localidades vecinas y la vista lejana con las ciudades de
 * referencia— y dos de "Descripción de suelos".
 *
 * NO incluye la satelital: esa vive en `informes.imagen`, se carga en
 * Antecedentes generales y es la única que se IMPRIME en la cotización. Ni las
 * fotografías del terreno, que son VARIAS por encuadre y tienen su propio
 * vocabulario ({@see TipoDeFotografiaDelInforme}).
 */
export type TipoDeImagenDelInforme =
  | "limites_generales"
  | "vias_acceso"
  | "vista_alejada"
  // Los dos de "Descripción de suelos". Comparten tabla y endpoints con los
  // tres de arriba porque comparten la CARDINALIDAD —una sola por informe—, no
  // la pestaña: qué encuadres pide cada una lo declaran las dos constantes de
  // abajo.
  | "mosaico_suelos"
  | "plano_regulador"
  // La portada SUBIDA A MANO (Antecedentes generales). Si existe gana sobre la
  // que manda la app de terreno; quitarla devuelve la de la visita.
  | "portada"
  // Los cuatro planos de los ANEXOS (tarjeta «Anexos» de Descripción de la
  // propiedad): una imagen cada uno.
  | "plano_roles_sii"
  | "area_estudio"
  | "plano_subdivision"
  | "mapa_referencias";

/** Los cuatro planos de la tarjeta «Anexos», con su rótulo. */
export const PLANOS_DE_ANEXOS: { tipo: TipoDeImagenDelInforme; etiqueta: string }[] = [
  { tipo: "plano_roles_sii", etiqueta: "Plano de roles del SII" },
  { tipo: "area_estudio", etiqueta: "Área de estudio" },
  { tipo: "plano_subdivision", etiqueta: "Plano de subdivisión" },
  { tipo: "mapa_referencias", etiqueta: "Mapa de las referencias de mercado" },
];

/**
 * Los tres, en el orden en que la pestaña los pide, con su rótulo y su ayuda.
 *
 * El ORDEN es el del prototipo y no es decorativo: va de lo cercano a lo
 * lejano, que es como se lee el capítulo del informe. Los rótulos viven acá y
 * no en el backend porque son texto de pantalla — allá solo se necesita el
 * código para validar y para armar la clave del objeto.
 */
export const IMAGENES_DEL_EXPEDIENTE: {
  tipo: TipoDeImagenDelInforme;
  etiqueta: string;
  ayuda: string;
}[] = [
  {
    tipo: "limites_generales",
    etiqueta: "Límites generales de la propiedad",
    ayuda: "El polígono del predio completo, con todos sus roles dentro.",
  },
  {
    tipo: "vias_acceso",
    etiqueta: "Vías de acceso y localidades cercanas",
    ayuda: "Por dónde se llega al predio y qué localidades tiene alrededor.",
  },
  {
    tipo: "vista_alejada",
    etiqueta: "Vista alejada · ciudades cercanas",
    ayuda: "La vista lejana que ubica el predio respecto de las ciudades de referencia.",
  },
];

/**
 * Los dos encuadres de "Descripción de suelos", con su rótulo y su ayuda.
 *
 * CONSTANTE APARTE Y NO UN AGREGADO A `IMAGENES_DEL_EXPEDIENTE`, aunque
 * compartan tipo, tabla y endpoints: cada pestaña dibuja un recuadro por cada
 * entrada de SU lista, así que una sola constante haría que la pestaña de la
 * propiedad pidiera el mosaico de suelos y al revés.
 */
export const IMAGENES_DE_SUELOS: {
  tipo: TipoDeImagenDelInforme;
  etiqueta: string;
  ayuda: string;
}[] = [
  {
    tipo: "mosaico_suelos",
    etiqueta: "Mosaico de clases de uso de suelo",
    ayuda: "La capa de clases de uso sobre el predio: la imagen del cuadro de arriba.",
  },
  {
    tipo: "plano_regulador",
    etiqueta: "Plano regulador",
    ayuda: "El instrumento de planificación de la comuna, con la zonificación del predio.",
  },
];

/**
 * Los encuadres del expediente que llevan VARIAS fotografías.
 *
 * La diferencia con {@see TipoDeImagenDelInforme} es la CARDINALIDAD y nada
 * más: aquellos se piden una vez y se REEMPLAZAN, estas se AGREGAN y se
 * QUITAN, porque de un mismo recorrido salen diez fotos del terreno.
 */
export type TipoDeFotografiaDelInforme = "terreno_topografia" | "inmueble" | "dron";

/** Las dos galerías de la tarjeta «Anexos». */
export const FOTOGRAFIAS_DE_ANEXOS: {
  tipo: TipoDeFotografiaDelInforme;
  etiqueta: string;
  ayuda: string;
}[] = [
  {
    tipo: "inmueble",
    etiqueta: "Fotografías del inmueble",
    ayuda: "Fotos generales de la propiedad. Cada una puede llevar su leyenda en el informe.",
  },
  {
    tipo: "dron",
    etiqueta: "Vistas con dron",
    ayuda: "Tomas aéreas del predio. Cada una puede llevar su leyenda en el informe.",
  },
];

export const FOTOGRAFIAS_DE_SUELOS: {
  tipo: TipoDeFotografiaDelInforme;
  etiqueta: string;
  ayuda: string;
}[] = [
  {
    tipo: "terreno_topografia",
    etiqueta: "Fotografías de terreno · topografía",
    ayuda: "Las fotos del recorrido: qué se ve del terreno y cómo cae. Respaldan la descripción de la topografía.",
  },
];

/** Una fotografía del expediente. A diferencia de un encuadre, tiene id. */
export interface FotografiaDeInforme {
  id: number;
  /** sha256 del contenido. Sirve de cache-buster en la url de lectura. */
  hash: string;
  bytes: number;
  /** El tipo REAL detectado del contenido, no el que declaró el navegador. */
  mime: string;
  subida_en: string | null;
  /** El pie libre que imprime el informe. `null` = el automático. */
  leyenda?: string | null;
}

/**
 * Una fila del cuadro "Clases de uso de suelo y superficie".
 *
 * Lleva las DOS: `suelo_clase_id` para volver a mostrar la clase marcada en el
 * selector, y `clase` —la copia CONGELADA— porque es lo que el informe
 * imprime. No son redundantes: el catálogo se corrige, y el día que difieran
 * manda la copia.
 */
export interface ClaseDeSueloDelInforme {
  id: number;
  suelo_clase_id: number;
  clase: string;
  descripcion: string | null;
  /**
   * En la unidad que declara el INFORME (`unidad_superficie`), no en hectáreas
   * fijas: el cuadro es un desglose del mismo predio, y su subtotal se compara
   * contra la superficie declarada.
   *
   * String porque el servidor la entrega como `decimal:2` — igual que las
   * superficies de los roles, y por lo mismo: pasarla por un número justo
   * antes de un cuadro que se suma es donde aparecen los desvíos.
   */
  superficie: string | null;
  validada: boolean;
  orden: number;
}

/** La imagen satelital ÚNICA del informe. */
export interface ImagenDeInforme {
  /** sha256 del contenido. Cambia con la imagen: sirve de cache-buster. */
  hash: string;
  bytes: number;
  /** El tipo REAL detectado del contenido, no el que declaró el navegador. */
  mime: string;
  subida_en: string | null;
}

/**
 * Las monedas en que se puede expresar un valor cargado a mano. Espejo de
 * `App\Enums\Moneda`.
 */
export type Moneda = "UF" | "USD" | "CLP";

/**
 * Las tres, en el orden del selector. USD va primero porque es lo corriente en
 * maquinaria agrícola importada, que es el grueso de lo que se carga acá.
 */
export const MONEDAS: { valor: Moneda; etiqueta: string }[] = [
  { valor: "USD", etiqueta: "USD" },
  { valor: "UF", etiqueta: "UF" },
  { valor: "CLP", etiqueta: "CLP" },
];

/**
 * Una foto que cuelga de UNA FILA de una pestaña técnica: una máquina, una
 * instalación, una construcción. Nunca la ruta del bucket.
 *
 * ES LA MISMA FORMA PARA LAS TRES, así que es un solo tipo: `FotosDeLaFila`
 * las dibuja sin saber de cuál viene, y el día que entre una cuarta pestaña
 * con fotos no hay nada que declarar.
 */
export interface FotoDeFila {
  id: number;
  /** sha256 del contenido. Sirve de cache-buster en la url de la miniatura. */
  hash: string;
  bytes: number;
  mime: string;
  subida_en: string | null;
  /** El pie libre que imprime el informe. `null` = el nombre de la fila. */
  leyenda?: string | null;
}

/** El nombre con el que la pestaña de maquinaria ya nombraba a {@link FotoDeFila}. */
export type FotoDeMaquinaria = FotoDeFila;

/**
 * Una instalación u obra complementaria: una losa, una red, un cierro.
 *
 * NO TIENE VIDA ÚTIL NI AJUSTE COMERCIAL, y las dos ausencias definen la
 * pestaña: los dos valores en UF se cargan derecho y son independientes entre
 * sí, mientras que en maquinaria y en construcciones el segundo se DERIVA del
 * primero con un porcentaje.
 *
 * TODO VA EN UF y no hay campo de moneda: la obra civil se presupuesta en UF
 * en Chile, y los rótulos de la pantalla lo dicen.
 */
export interface InstalacionDeInforme {
  id: number;
  tipo: string;
  anio: number | null;
  /** DECIMAL: las unidades de una obra se miden (m², ml), no se cuentan. */
  cantidad: string | null;
  valor_reposicion_uf_un: string | null;
  valor_comercial_uf_un: string | null;
  /** Lo que quien fue a ver anotó sobre ESTA obra. */
  nota_terreno: string | null;
  validada: boolean;
  /**
   * El uuid de la instalación que el encargado creó en la App y esta fila
   * adoptó, o `null`. De ahí cuelgan sus registros de terreno; es único por
   * informe.
   */
  terreno_ref: string | null;
  /**
   * Cuándo alguien decidió «Mantener» tras el aviso de terreno «No está en el
   * predio», o `null` si nadie lo decidió. El aviso solo vuelve si el encargado
   * registra una ausencia más nueva (`ItemDeTerreno.ausente_desde`).
   */
  ausencia_mantenida_en?: string | null;
  /** `null` cuando falta el unitario o la cantidad — NUNCA `0`. */
  total_reposicion_uf: number | null;
  total_comercial_uf: number | null;
  fotos?: FotoDeFila[];
}

/**
 * Una construcción: la casa patronal, el galpón, la bodega.
 *
 * ES LA GEMELA DE `MaquinariaDeInforme` con el multiplicador cambiado: allá
 * son unidades y acá METROS CUADRADOS. Los valores van en UF POR METRO
 * CUADRADO, como dicen los rótulos del formulario.
 */
export interface ConstruccionDeInforme {
  id: number;
  grupo: string | null;
  tipo_edificacion: string;
  anio: number | null;
  superficie_m2: string | null;
  clase: string | null;
  vida_util_total: number | null;
  vida_util_residual: number | null;
  valor_reposicion_uf_m2: string | null;
  valor_residual_uf_m2: string | null;
  ajuste_comercial_pct: string | null;
  nota_terreno: string | null;
  validada: boolean;
  /**
   * El uuid de la construcción de la App de terreno de la que nació esta fila,
   * o `null` si se cargó en la web. Con él el registro de terreno cuelga de la
   * fila (fotos, audios, notas por dato).
   */
  terreno_ref: string | null;
  total_reposicion_uf: number | null;
  total_residual_uf: number | null;
  /** La columna "TOTAL UF": el residual con el ajuste comercial aplicado. */
  total_uf: number | null;
  fotos?: FotoDeFila[];
}

/**
 * Una plantación: un cuartel de cerezos Sweet Heart de 4,30 há plantado en
 * 2016 a 1.111 plantas por hectárea.
 *
 * NO TIENE NINGÚN VALOR, y eso la separa de las otras tres filas con cuadro.
 * Es un DESCRIPTOR: registra qué hay plantado y con qué densidad, no cuánto
 * vale. Lo que aporta al predio sale del método de activos biológicos
 * (IFRS NIC 41), que todavía no está construido.
 *
 * `especie` ES LA CLAVE CON LA QUE EL CUADRO AGRUPA, y llega tal como se
 * escribió. La agrupación se arma acá en el front (ver
 * `components/informes/plantaciones.ts`): el servidor devuelve las filas en el
 * orden en que se cargaron y no sabe nada de grupos.
 */
export interface PlantacionDeInforme {
  id: number;
  especie: string;
  variedad: string | null;
  anio_plantacion: number | null;
  /**
   * EN HECTÁREAS SIEMPRE, no en la unidad del informe.
   *
   * Su columna vecina es la densidad en plantas POR HECTÁREA, así que el
   * producto solo cierra si las dos hablan de lo mismo. La unidad está impresa
   * en el rótulo del campo, en el encabezado de la columna y en los totales.
   */
  superficie_ha: string | null;
  cuartel: string | null;
  portainjerto: string | null;
  /** "4,5 x 2,0": entre hileras y entre plantas, leídas como una expresión. */
  distancia_plantacion: string | null;
  plantas_por_ha: number | null;
  sistema_riego: string | null;
  /** Lo que quien fue a ver anotó sobre ESTE cuartel. */
  nota_terreno: string | null;
  validada: boolean;
  /**
   * El uuid de la plantación que el encargado creó en la App y esta fila
   * adoptó, o `null`. De ahí cuelgan sus registros de terreno; es único por
   * informe.
   */
  terreno_ref: string | null;
  /**
   * Superficie × densidad, YA REDONDEADA por el servidor.
   *
   * QUE VENGA REDONDEADA NO ES UN DETALLE: los subtotales del cuadro suman
   * ESTA cifra, no los productos exactos, y así es como el pie de cada especie
   * coincide con el del prototipo (las dos cuentas difieren en una planta).
   *
   * `null` —y nunca `0`— cuando falta la superficie o la densidad.
   */
  plantas_totales: number | null;
  fotos?: FotoDeFila[];
}

/**
 * Una máquina o equipo de la propiedad.
 *
 * LOS DECIMALES VIAJAN COMO STRING —es la convención del repo para las
 * columnas `decimal:*`— y LOS TOTALES COMO NÚMERO, porque los calcula el
 * servidor. La pantalla no recalcula ninguno de los tres: no hay gemelo en
 * TypeScript de esa aritmética, a diferencia de `homologacion.ts`.
 */
export interface MaquinariaDeInforme {
  id: number;
  nombre: string;
  anio_fabricacion: number | null;
  unidades: number;
  vida_util_total: number | null;
  vida_util_residual: number | null;
  valor_unitario_reposicion: string | null;
  valor_unitario_residual: string | null;
  ajuste_comercial_pct: string | null;
  moneda: Moneda | null;
  /** Quién se hace responsable de esta fila (la casilla "Validar"). */
  validada: boolean;
  /**
   * El uuid de la máquina que el encargado creó en la App y esta fila adoptó,
   * o `null`. De ahí cuelgan sus registros de terreno; es único por informe.
   */
  terreno_ref: string | null;
  /**
   * Cuándo alguien decidió «Mantener» tras el aviso de terreno «No está en el
   * predio», o `null` si nadie lo decidió. El aviso solo vuelve si el encargado
   * registra una ausencia más nueva (`ItemDeTerreno.ausente_desde`).
   */
  ausencia_mantenida_en?: string | null;
  /** `null` cuando falta el valor unitario — NUNCA `0`. */
  valor_reposicion_total: number | null;
  valor_residual_total: number | null;
  valor_comercial_total: number | null;
  fotos?: FotoDeMaquinaria[];
}

/**
 * El estado guardado de UNA pestaña, más allá de sus notas.
 *
 * `no_aplica` tiene TRES estados y el `null` es uno de ellos: significa que
 * nadie abrió la pestaña todavía, distinto de `false` ("hay, y está en la
 * lista"). La casilla se dibuja apagada en los dos casos, pero el semáforo de
 * completitud sí los separa.
 */
export interface EstadoDeSeccion {
  no_aplica: boolean | null;
  descripcion_general: string | null;
  descripcion_general_validada: boolean;
}

export interface Informe {
  id: number;
  estado: EstadoInforme;
  /**
   * El número del informe en el ERP de Consultek, donde se administran los
   * datos de los clientes. Lo asigna el ERP y acá se transcribe.
   *
   * Hoy es obligatorio al crear y al editar, pero el tipo sigue admitiendo
   * `null`: los informes creados antes de esa regla existen así, y la pestaña
   * de edición tiene que poder mostrarlos para que alguien los complete.
   */
  numero_informe: string | null;
  cliente_nombre: string | null;
  /** El cargo de quien recibe la carta. `null` = no se imprime el renglón. */
  cargo_cliente: string | null;
  telefono_contacto: string | null;
  /** A donde se envía la cotización aprobada (RF-G3). Copia congelada. */
  email_contacto: string | null;
  solicitante_nombre: string | null;
  propiedad_nombre: string | null;
  direccion: string | null;
  superficie_ha: string | null;
  /**
   * La coordenada del predio, que el módulo de referencias de mercado usa como
   * ORIGEN de la distancia. Llega como string (`decimal:6`) y no se recalcula.
   *
   * Las dos juntas o ninguna: media coordenada no ubica nada.
   */
  latitud: string | null;
  longitud: string | null;
  descripcion_ubicacion: string | null;
  descripcion: string | null;
  descripcion_ubicacion_validada: boolean;
  descripcion_validada: boolean;
  /** `null` = "Sin verificar" (RF-E3), distinto de `false` = sin coincidencias. */
  es_retasacion: boolean | null;
  creado_por: number | null;
  created_at: string;
  /* Las relaciones viajan con `whenLoaded`: el LISTADO no las trae, la ficha
     sí. Por eso son opcionales y no arreglos vacíos. */
  /**
   * Los roles del informe, en el orden del documento — la ÚNICA vista: ya no
   * hay un segundo arreglo agrupado por bloque/propiedad que reconciliar con
   * este. Es lo que leen el formulario, la ficha de visado y el documento de
   * cotización.
   */
  roles?: RolDeInforme[];
  /**
   * La unidad de la columna Superficie de la tabla de roles y de su TOTAL. En
   * el documento real es del CUADRO entero y no de cada fila.
   */
  unidad_superficie?: "ha" | "m2";
  /**
   * La nota que acompaña la imagen satelital, aclarando lo que no muestra —
   * por ejemplo, que los roles caen en dos comunas distintas y la imagen solo
   * puede encuadrar una.
   */
  nota_imagen?: string | null;
  /**
   * La imagen satelital ÚNICA del informe, o `null` si todavía no tiene — un
   * estado normal, no incompleto: se consigue mientras se prepara el
   * presupuesto.
   *
   * NO trae la ruta del bucket, que es privada: para mostrarla se pide a
   * `urlDeLaImagenDelInforme()`, que arma la URL del endpoint autenticado.
   */
  imagen?: ImagenDeInforme | null;
  /**
   * Los encuadres de UNA SOLA imagen del expediente, como MAPA tipo → imagen.
   *
   * TODAS las claves vienen siempre, con `null` en las que todavía no se
   * cargaron: la pantalla dibuja un recuadro por encuadre existan o no, y con
   * una lista tendría que buscar dentro para saber si le toca dibujar el
   * vacío. Es el mismo criterio que `notas` y `responsables`.
   */
  imagenes?: Partial<Record<TipoDeImagenDelInforme, ImagenDeInforme | null>>;
  /**
   * Las fotografías del expediente, como MAPA encuadre → LISTA.
   *
   * Mapa por lo mismo que `imagenes`; lista adentro porque son varias, que es
   * toda la diferencia con el mapa de arriba.
   */
  fotografias?: Partial<Record<TipoDeFotografiaDelInforme, FotografiaDeInforme[]>>;

  /* --- Pestaña "Descripción de suelos" --- */

  /**
   * "15 há planas de uso agrícola y 8 há de cerros con pendiente pronunciada".
   * Es PROSA y no dos números: lo que el informe imprime es la descripción.
   */
  topografia?: string | null;
  topografia_validada?: boolean;
  /**
   * La zonificación y la normativa según el plano regulador.
   *
   * SIN casilla "validada", al revés que los dos de arriba, y no es un olvido:
   * la casilla existe porque el texto lo puede haber propuesto la IA, y de los
   * tres este es el único que la pantalla no ofrece mejorar.
   */
  descripcion_plano_regulador?: string | null;
  /**
   * El semestre del avalúo fiscal ("1er. Sem. 2026"): rotula la columna
   * «Avalúo fiscal» de la tabla legal de roles. Texto libre, uno por informe.
   */
  avaluo_fiscal_periodo?: string | null;
  /** El cuadro "Clases de uso de suelo y superficie", ya ordenado. */
  clases_de_suelo?: ClaseDeSueloDelInforme[];
  /**
   * Las fuentes de agua del predio, en el orden en que la pantalla las dejó.
   *
   * DOS LISTAS Y NO UNA, porque responden preguntas distintas: la fuente dice
   * CUÁNTA agua hay —por eso se suma en l/s— y la obra dice cómo se aprovecha
   * la que ya se tiene, sin agregar un litro.
   */
  agua_fuentes?: FuenteDeAguaDelInforme[];
  /** Las obras de aprovechamiento, en el orden en que la pantalla las dejó. */
  agua_obras?: ObraDeAguaDelInforme[];
  /**
   * La plantilla elegida y los ítems que el informe lleva. `plantilla` es
   * `null` en un informe ANTERIOR a las plantillas: ese solo tiene la lista
   * plana `contenidos`, que se conserva para leerla y no se edita.
   */
  plantilla?: { id: number; nombre: string } | null;
  /** El formato de salida elegido; `null` en un informe anterior a los formatos. */
  formato_salida?: { id: number; nombre: string } | null;
  items?: ItemDelInforme[];
  contenidos?: { id: number; nombre: string; orden: number }[];
  documentos?: {
    documento_tipo_id: number;
    obligatoriedad: Obligatoriedad;
    /**
     * El nombre y la categoría del TIPO, para que la pantalla de cotización
     * pueda ofrecer estos mismos documentos sin traerse el maestro entero.
     */
    nombre?: string | null;
    categoria?: CategoriaDocumento | null;
  }[];
  /**
   * Los archivos RECIBIDOS, de todas las categorías, en una lista plana. La
   * pantalla los agrupa por `documento_tipo_id`.
   */
  archivos?: ArchivoDeAntecedente[];
  /**
   * Los datos con los que se le factura, cargados por el cliente en su portal.
   *
   * `null` SIGNIFICA que nadie los mandó todavía, y no es lo mismo que un
   * registro en blanco: la pantalla lo dice distinto.
   */
  facturacion?: FacturacionDelInforme | null;
  /**
   * Cuándo abrió el cliente su portal por última vez, o `null` si nunca entró.
   *
   * Es lo único que responde "¿vio el correo?" cuando hay que decidir entre
   * esperar y levantar el teléfono.
   */
  portal_visitado_en?: string | null;
  /** Mapa pestaña → notas. Todas las claves vienen siempre; `null` = sin notas. */
  notas?: Record<SeccionInforme, string | null>;
  /**
   * EL RESTO del estado por pestaña, con la misma forma de mapa.
   *
   * Va al lado de `notas` y no adentro aunque las cuatro columnas salgan de la
   * misma fila: es la regla con que `comuna_codigo` se sumó junto a `comuna`.
   * No se pueden contradecir porque no comparten ninguna columna.
   */
  secciones?: Record<SeccionInforme, EstadoDeSeccion>;
  /** La maquinaria del predio, en el orden en que se cargó. */
  maquinarias?: MaquinariaDeInforme[];
  /** Las instalaciones y obras complementarias, en el orden en que se cargaron. */
  instalaciones?: InstalacionDeInforme[];
  /** Las construcciones, en el orden en que se cargaron. */
  construcciones?: ConstruccionDeInforme[];
  /**
   * Las plantaciones, en el orden en que se cargaron.
   *
   * SIN AGRUPAR: el cuadro se ve agrupado por especie y eso lo arma la
   * pantalla. Si el servidor las ordenara por especie, corregir un nombre
   * movería de lugar las filas mientras alguien escribe.
   */
  plantaciones?: PlantacionDeInforme[];
  /** La cotización VIGENTE, o `null` si todavía no se cotizó. */
  cotizacion?: Cotizacion | null;
  responsables?: Record<RolResponsable, number | null>;
  /** Quién visa esta cotización (RF-G2). `null` = todavía sin designar. */
  revisor_cotizacion?: { id: number; nombre: string } | null;
  /**
   * Si QUIEN PIDIÓ puede visar esta cotización (RF-G2/RF-G3): el revisor de la
   * cotización designado en ESTE informe, o el Super Admin.
   *
   * Lo calcula el servidor y viaja resuelto a propósito. Reproducir acá la
   * regla —perfil + revisor designado— la duplicaría, y la copia que se
   * atrasara ofrecería un botón que el 403 rechaza al apretarlo.
   *
   * Opcional porque solo la FICHA la trae; el listado no la pide.
   */
  puede_visar?: boolean;
  /** La app de terreno: si el informe ya se le envió al encargado de HOY. */
  terreno: TerrenoDelInforme;
}

/**
 * El estado del informe en la app de terreno.
 *
 * `disponible` es la misma pregunta que se hace la compuerta de la app: una
 * habilitación enviada al encargado ANTERIOR no cuenta. `puede_gestionar` y
 * `motivo_no_habilitable` los calcula el servidor y solo viajan en la ficha,
 * igual que `puede_visar`: reproducir acá la regla la duplicaría.
 */
/**
 * Las etapas en que un informe puede ir a la app de terreno. Gemelo de
 * `Informe::ESTADOS_DE_TERRENO`: antes de la aceptación no hay visita, y un
 * informe finalizado ya no la necesita.
 */
export function admiteTerreno(estado: EstadoInforme): boolean {
  return estado === "en_proceso" || estado === "en_espera_revision_final";
}

export interface TerrenoDelInforme {
  disponible: boolean;
  habilitado_en: string | null;
  habilitado_para: { id: number; nombre: string | null } | null;
  puede_gestionar?: boolean;
  motivo_no_habilitable?: string | null;
}

/** Cómo está cada sección de la guía; los rótulos y colores los elige la pantalla. */
export type EstadoDeSeccionDeLaGuia =
  | "con_datos"
  | "sin_datos"
  | "no_aplica"
  | "por_solicitar"
  | "completo";

/**
 * La guía de validación en terreno (`GET /informes/{id}/guia-terreno`).
 *
 * La arma el servidor una sola vez y la dibujan dos: la modal de la ficha y
 * el PDF que viaja en el correo. Por eso esta pantalla no calcula nada: solo
 * la muestra.
 */
export interface GuiaDeTerreno {
  encabezado: {
    propiedad: string | null;
    cliente: string | null;
    numero_informe: string | null;
    encargado: string | null;
    ejecutivo: string | null;
    disponible_en_terreno: boolean;
  };
  secciones: {
    clave: string;
    titulo: string;
    filas: { nombre: string; detalle: string; pide_foto: boolean }[];
    instruccion: string;
    pregunta: string;
    estado: EstadoDeSeccionDeLaGuia;
    cantidad: number;
    sin_datos_texto: string | null;
  }[];
  pendientes: { texto: string }[];
}

export async function fetchGuiaDeTerreno(informeId: number): Promise<GuiaDeTerreno> {
  const respuesta = await apiFetch<{ data: GuiaDeTerreno }>(`/api/informes/${informeId}/guia-terreno`);
  return respuesta.data;
}

/** Mismo mecanismo que la cotización: el nombre del archivo lo decide el servidor. */
export async function descargarGuiaDeTerrenoRequest(informeId: number): Promise<void> {
  await descargarArchivo(
    `/api/informes/${informeId}/guia-terreno/pdf`,
    `guia-de-terreno-informe-${informeId}.pdf`,
  );
}

/**
 * Envía el informe a la app del encargado y le manda la guía por correo.
 *
 * `correo_enviado: false` NO es un fracaso: el informe quedó habilitado y lo
 * que falló fue el aviso. La pantalla lo dice para que se reenvíe.
 */
export async function enviarATerrenoRequest(
  informeId: number,
): Promise<{ correo_enviado: boolean; data: Informe }> {
  return apiFetch<{ correo_enviado: boolean; data: Informe }>(`/api/informes/${informeId}/terreno`, {
    method: "POST",
  });
}

export async function retirarDeTerrenoRequest(informeId: number): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${informeId}/terreno`, {
    method: "DELETE",
  });
  return respuesta.data;
}

/**
 * Cada cuántos milisegundos la ficha se pide sola mientras algo esté EN
 * CURSO (`odd/tasks/transcripcion-en-limbo.md`).
 *
 * 5 s y no menos: es la misma cadencia con la que un humano refrescaría a
 * mano si estuviera esperando, y no vale la pena pedir más seguido una ficha
 * completa por un trabajo que en el mejor caso tarda segundos y en el peor
 * decenas de minutos.
 */
export const INTERVALO_DE_POLL_DEL_CONTEXTO_MS = 5000;

/**
 * Si la ficha debe seguir pidiéndose sola.
 *
 * ES LA ÚNICA PREGUNTA que decide el `refetchInterval` de `useInforme`
 * (función, no un número fijo): sondear siempre —incluso con el expediente
 * entero ya procesado— dispararía peticiones para siempre. Las filas
 * TRABADAS —`contexto_en_curso: false`, incluidas las de antes de este
 * cambio, que nunca se tocaron— NO cuentan: el servidor ya decidió que ahí no
 * hay nada corriendo, así que sondearlas no las va a mover solas.
 *
 * Toma un `Informe | undefined` (parcial, con solo `archivos`) y no una lista
 * suelta: es la misma forma que entrega `query.state.data` en
 * `refetchInterval`, así que `useInforme` se la pasa tal cual.
 */
export function debePollearElContexto(
  informe: { archivos?: { contexto_en_curso: boolean }[] } | undefined,
): boolean {
  return (informe?.archivos ?? []).some((archivo) => archivo.contexto_en_curso);
}

export interface CrearInformeInput {
  /** Obligatorio: es la clave con la que el informe se cruza contra el ERP. */
  numero_informe: string;
  cliente_nombre: string;
  /** El cargo de quien recibe la carta. `null` = no se imprime el renglón. */
  cargo_cliente: string | null;
  telefono_contacto: string;
  email_contacto: string;
  solicitante_nombre: string;
  propiedad_nombre: string;
  direccion: string | null;
  /**
   * La coordenada del predio, que el tasador marca en un mapa.
   *
   * LAS DOS JUNTAS O NINGUNA — el servidor rechaza media coordenada con un
   * 422. En el formulario eso es imposible de escribir: ahí viven como UN
   * objeto nullable y se desarman recién al enviar.
   */
  latitud: number | null;
  longitud: number | null;
  descripcion_ubicacion: string | null;
  descripcion: string | null;
  /**
   * El formato de salida elegido, compatible con la plantilla. Reemplazó al
   * uso de la tasación (retirado el 08-10-2026).
   */
  formato_salida_id: number;
  /** `null` = "Sin verificar" (RF-E3). NO es lo mismo que `false`. */
  es_retasacion: boolean | null;
  /** La unidad del encabezado de la columna Superficie y de su TOTAL. */
  unidad_superficie: "ha" | "m2";
  /** La nota que acompaña la imagen satelital. `null` = sin nota. */
  nota_imagen: string | null;
  /**
   * Los roles del informe, REEMPLAZADOS enteros al guardar: no llevan `id` ni
   * `orden` porque no tienen identidad propia para el usuario —se edita una
   * lista, no se reconcilian una por una— y el `orden` sale de la POSICIÓN en
   * este arreglo.
   *
   * `comuna_codigo` es lo que se elige del maestro. NUNCA viaja `comuna`: el
   * servidor la resuelve contra el maestro de comunas y una copia mandada acá
   * sería ignorada en el mejor de los casos — el nombre del informe no lo
   * decide el front.
   */
  roles: {
    /**
     * CUÁL de los roles que ya existen es este, para que el servidor
     * RECONCILIE en vez de borrar y recrear.
     *
     * Solo lo manda la EDICIÓN, y el alta lo rechaza con un 422: ahí no hay
     * ningún rol al que referirse. Existe desde que el rol es dueño de la
     * imagen de su límite y de la descripción de sus deslindes — sin él,
     * guardar el nombre del cliente se llevaría el trabajo de la pestaña
     * "Descripción de la propiedad" sin que nada fallara.
     *
     * Ausente en un rol recién agregado en la pantalla, y esa ausencia es
     * exactamente lo que significa "créalo".
     */
    id?: number;
    rol_sii: string;
    comuna_codigo: string | null;
    /**
     * `null` NO significa lo mismo en las dos: terreno vacío se imprime "—"
     * (no corresponde) y construida vacía "s/i" (nadie la midió).
     */
    superficie: number | null;
    superficie_construida: number | null;
  }[];
  plantilla_id: number;
  /** ids del maestro, en el orden de la plantilla. */
  items: number[];
  responsables: Record<RolResponsable, number>;
}

/* --------------------------------------------------------------------------
   Listado de Informes en proceso (pantalla `16 UN`)
   -------------------------------------------------------------------------- */

/** Quién tiene HOY el informe. `null` = todavía sin ejecutivo asignado. */
export interface EjecutivoDelInforme {
  id: number;
  nombre: string;
}

/** Las seis etapas del informe, en orden. El `indice` del resumen apunta a esta lista. */
export const ETAPAS_DEL_INFORME = [
  "Creado",
  "Terreno",
  "Antecedentes",
  "Generación",
  "En visado",
  "Enviado",
] as const;

export type ClaveDeEtapa =
  | "creado"
  | "terreno"
  | "antecedentes"
  | "generacion"
  | "visado"
  | "enviado";

/** Los cuatro roles del equipo de un informe, con la clave que usa el API. */
export type RolDelEquipo =
  | "ejecutivo"
  | "encargado_terreno"
  | "revisor_cotizacion"
  | "revisor_informe";

/** Rótulo de cada rol, en el orden en que se lista. */
export const ROLES_DEL_EQUIPO: { clave: RolDelEquipo; etiqueta: string }[] = [
  { clave: "ejecutivo", etiqueta: "Ejecutivo a cargo" },
  { clave: "encargado_terreno", etiqueta: "Encargado de terreno" },
  { clave: "revisor_cotizacion", etiqueta: "Revisor de la cotización" },
  { clave: "revisor_informe", etiqueta: "Revisor del informe final" },
];

/**
 * Lo que el listado resume de cada informe (`resumen` de cada fila).
 *
 * La etapa, el turno y la entrega se DERIVAN en el servidor, en un solo punto:
 * el front solo los dibuja. La fecha de entrega nunca se calcula a partir del
 * plazo (ver CLAUDE.md raíz: el plazo corre desde la visita a terreno).
 */
export interface ResumenDelInforme {
  etapa: { indice: number; clave: ClaveDeEtapa };
  /** Quién tiene el informe en las manos ahora; `null` si ya se envió. */
  le_toca: RolDelEquipo | null;
  equipo: Record<RolDelEquipo, EjecutivoDelInforme | null>;
  entrega: {
    fecha: string | null;
    origen: "estimada" | "comprometida" | null;
    plazo_dias_habiles: number | null;
    atrasado: boolean;
    /** Días que faltan para la fecha (negativo = ya pasó). `null` sin fecha. */
    dias: number | null;
  };
  novedades_app: number;
  dudas_ia: number;
  documentos: number;
  documentos_por_clasificar: number;
  ultima_actividad: string;
}

/**
 * Fila del listado. Es el informe con el ejecutivo resuelto y su `resumen`: las
 * otras relaciones no viajan en este endpoint, por eso siguen opcionales en
 * `Informe` y acá no se agregan.
 */
export interface InformeEnProceso extends Informe {
  ejecutivo: EjecutivoDelInforme | null;
  resumen: ResumenDelInforme;
}

/** Qué informes se listan: cada pestaña es un grupo de etapas. */
export type TabDeInformes = "activos" | "creados" | "visado" | "enviados" | "todos";

/** En qué rol debe estar la persona elegida. */
export type RolDeFiltro = "cualquiera" | "le_toca" | RolDelEquipo;

/** Los chips de atención: lo que pide mirar un informe hoy. */
export type AtencionDeInformes =
  | "atrasados"
  | "novedades_app"
  | "dudas_ia"
  | "anticipo_pendiente";

/**
 * Los criterios del select "Ordenar por". `fecha` es la fecha de creación, del
 * más nuevo al más viejo, y es el defecto (decisión de producto del 21-10-2026).
 */
export type OrdenDeInformes = "fecha" | "entrega" | "actividad";

export interface FiltrosDeInformes {
  q?: string;
  tab?: TabDeInformes;
  personaId?: number;
  rol?: RolDeFiltro;
  atencion?: AtencionDeInformes;
  orden?: OrdenDeInformes;
  pagina?: number;
}

export interface ConteosDelListado {
  /** Cuántos hay en cada pestaña, sobre la búsqueda y la persona elegidas. */
  tabs: Record<TabDeInformes, number>;
  /** Cuántos piden atención, dentro de la pestaña abierta. */
  atencion: Record<AtencionDeInformes, number>;
}

export interface PaginaDeInformes {
  informes: InformeEnProceso[];
  /** Cuántos quedaron DESPUÉS de los filtros: el "de M" del pie. */
  total: number;
  /** Cuántos hay en total sin filtrar: el "M" de "Mostrando N de M informes". */
  totalGeneral: number;
  conteos: ConteosDelListado;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

interface RespuestaPaginada {
  data: InformeEnProceso[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    total_general: number;
    conteos?: ConteosDelListado;
  };
}

const CONTEOS_VACIOS: ConteosDelListado = {
  tabs: { activos: 0, creados: 0, visado: 0, enviados: 0, todos: 0 },
  atencion: { atrasados: 0, novedades_app: 0, dudas_ia: 0, anticipo_pendiente: 0 },
};

/** Estado del informe que NO se lista en esta pantalla. */
export type EstadoEnProceso = Exclude<EstadoInforme, "finalizado">;

/**
 * Los estados que esta pantalla lista, con el texto EXACTO del prototipo
 * `18-08-2026` — son vocabulario del negocio, no rótulos decorativos.
 *
 * `finalizado` no está: el listado lo deja fuera y el backend rechaza
 * filtrarlo. `incompleto` SÍ está, aunque el prototipo no lo ofrezca en su
 * select: sus filas pueden estarlo (RF-E1 §8.3 guarda borradores) y un filtro
 * que no alcanza un estado visible deja informes imposibles de encontrar.
 */
export const ESTADOS_EN_PROCESO: { estado: EstadoEnProceso; etiqueta: string }[] = [
  { estado: "incompleto", etiqueta: "Incompleto" },
  { estado: "creado", etiqueta: "Creado" },
  { estado: "cotizacion_en_revision", etiqueta: "Cotización generada · enviada a revisión" },
  { estado: "cotizacion_enviada_cliente", etiqueta: "Enviada al cliente · en espera de aceptación" },
  { estado: "en_proceso", etiqueta: "En proceso" },
  { estado: "en_espera_revision_final", etiqueta: "En espera de revisión final" },
];

export const ETIQUETA_ESTADO: Record<EstadoInforme, string> = {
  ...Object.fromEntries(ESTADOS_EN_PROCESO.map(({ estado, etiqueta }) => [estado, etiqueta])),
  finalizado: "Informe final aprobado",
} as Record<EstadoInforme, string>;

/**
 * El rótulo CORTO del estado, para el badge de la tabla de informes en proceso.
 *
 * Los de `ETIQUETA_ESTADO` van de "Creado" a "Enviada al cliente · en espera
 * de aceptación": cuatro veces más largos entre sí, y en una columna de badges
 * eso la descuadra. Acá van de una o dos palabras; lo que el rótulo corto deja
 * fuera lo explica `DESCRIPCION_ESTADO`, en el tooltip.
 *
 * "En visado" y no "En revisión": es la palabra de la pantalla del revisor
 * ("Visado de cotización"), y deja "revisión" libre para la revisión FINAL,
 * que es otra etapa.
 */
export const ETIQUETA_CORTA_ESTADO_INFORME: Record<EstadoInforme, string> = {
  incompleto: "Incompleto",
  creado: "Creado",
  cotizacion_en_revision: "En visado",
  cotizacion_enviada_cliente: "Enviada",
  en_proceso: "En proceso",
  en_espera_revision_final: "Revisión final",
  finalizado: "Aprobado",
};

/** Qué significa cada estado, en una frase: el tooltip del rótulo corto. */
export const DESCRIPCION_ESTADO: Record<EstadoInforme, string> = {
  incompleto: "Borrador con datos pendientes. Hay que completarlo antes de cotizar.",
  creado: "El informe está creado y todavía no tiene cotización.",
  cotizacion_en_revision: "La cotización está generada y espera el visado de un revisor.",
  cotizacion_enviada_cliente: "La cotización se envió al cliente y espera su aceptación.",
  en_proceso: "El cliente aceptó la cotización y la tasación está en curso.",
  en_espera_revision_final: "El informe está terminado y espera la revisión final.",
  finalizado: "El informe final está aprobado.",
};

/**
 * Color del badge de estado, por SIGNIFICADO y no por posición en la máquina
 * de estados:
 *
 * - `info` — el informe está listo y esperando que alguien lo tome.
 * - `warning` — hay algo pendiente de OTRA persona (el revisor, el cliente).
 * - `success` — la cotización fue aceptada y el trabajo real está en curso.
 * - neutro — el borrador todavía no empezó. El prototipo lo pinta de ámbar,
 *   pero eso es la rama `else` de su ternario y no una decisión: un borrador
 *   a medias no reclama atención de nadie, así que no compite con los que sí.
 */
export const VARIANTE_ESTADO_INFORME: Record<EstadoInforme, "info" | "warning" | "success" | "outline"> = {
  incompleto: "outline",
  creado: "info",
  cotizacion_en_revision: "warning",
  cotizacion_enviada_cliente: "warning",
  en_proceso: "success",
  en_espera_revision_final: "warning",
  finalizado: "success",
};

/**
 * La edición manda lo mismo que el alta más lo que solo existe en la pestaña
 * ya creada: las dos casillas de validación y las notas al pie.
 */
export interface ActualizarInformeInput extends CrearInformeInput {
  descripcion_ubicacion_validada: boolean;
  descripcion_validada: boolean;
  notas_observaciones: string | null;
}

export async function fetchCatalogosDelInforme(): Promise<CatalogosDelInforme> {
  const respuesta = await apiFetch<{ data: CatalogosDelInforme }>("/api/informes/catalogos");
  return respuesta.data;
}

export async function fetchInformesEnProceso(
  filtros: FiltrosDeInformes = {},
): Promise<PaginaDeInformes> {
  const parametros = new URLSearchParams();
  // Solo lo que el usuario eligió: mandar un parámetro vacío haría que el
  // backend lo tome por presente y valide un string que no corresponde.
  if (filtros.q?.trim()) parametros.set("q", filtros.q.trim());
  if (filtros.tab && filtros.tab !== "activos") parametros.set("tab", filtros.tab);
  if (filtros.personaId) {
    parametros.set("persona_id", String(filtros.personaId));
    // `rol` sin persona no filtra nada: solo viaja acompañando a una.
    if (filtros.rol && filtros.rol !== "cualquiera") parametros.set("rol", filtros.rol);
  }
  if (filtros.atencion) parametros.set("atencion", filtros.atencion);
  if (filtros.orden && filtros.orden !== "fecha") parametros.set("orden", filtros.orden);
  if (filtros.pagina && filtros.pagina > 1) parametros.set("page", String(filtros.pagina));

  const cadena = parametros.toString();
  const respuesta = await apiFetch<RespuestaPaginada>(
    `/api/informes${cadena ? `?${cadena}` : ""}`,
  );

  return {
    informes: respuesta.data,
    total: respuesta.meta.total,
    totalGeneral: respuesta.meta.total_general,
    conteos: respuesta.meta.conteos ?? CONTEOS_VACIOS,
    pagina: respuesta.meta.current_page,
    porPagina: respuesta.meta.per_page,
    totalPaginas: respuesta.meta.last_page,
  };
}

export async function crearInformeRequest(input: CrearInformeInput): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>("/api/informes", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return respuesta.data;
}

export async function fetchInforme(id: number): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${id}`);
  return respuesta.data;
}

/**
 * Sube la imagen satelital ÚNICA del informe, reemplazando la que hubiera.
 *
 * `FormData` y no JSON: un archivo en un cuerpo JSON habría que codificarlo en
 * base64, lo que lo agranda un tercio y obliga a leerlo entero en memoria de
 * los dos lados. `apiFetch` reconoce el `FormData` y deja que el navegador
 * ponga el `Content-Type` con su boundary (ver `sendRequest` en `api.ts`).
 */
export async function subirImagenDelInformeRequest(
  informeId: number,
  archivo: File,
): Promise<Informe> {
  const cuerpo = new FormData();
  cuerpo.append("imagen", archivo);

  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${informeId}/imagen`, {
    method: "POST",
    body: cuerpo,
  });
  return respuesta.data;
}

export async function quitarImagenDelInformeRequest(informeId: number): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${informeId}/imagen`, {
    method: "DELETE",
  });
  return respuesta.data;
}

/**
 * La URL para MOSTRAR la imagen en un `<img src>`.
 *
 * Va por `urlDelApi` y no por `apiFetch` por lo mismo que el PDF de la
 * cotización: bajar la imagen con fetch obligaría a leer el binario a memoria
 * y envolverlo en un blob para terminar haciendo lo que el navegador ya hace
 * solo. La cookie de sesión se comparte entre `app.*` y `api.*`, así que el
 * `<img>` viaja autenticado.
 *
 * El `hash` va de sufijo y NO es decoración: la respuesta se cachea una hora,
 * así que sin él el navegador seguiría mostrando la imagen anterior después de
 * un reemplazo. Como el hash cambia con el contenido, la URL cambia con él.
 */
export function urlDeLaImagenDelInforme(informeId: number, hash: string): string {
  return urlDelApi(`/api/informes/${informeId}/imagen?v=${hash}`);
}

/* --------------------------------------------------------------------------
   Pestaña "Descripción de la propiedad"
   -------------------------------------------------------------------------- */

/**
 * Lo que se guarda con el botón "Guardar cambios" de la pestaña.
 *
 * SOLO TEXTO: las imágenes NO viajan acá. Cada una se sube al soltarla, en su
 * propia petición con su archivo, así que adjuntar una no obliga a reenviar
 * todo el texto de la pantalla ni al revés.
 *
 * Tampoco crea, borra ni reordena roles — eso es de "Antecedentes generales".
 * Acá cada entrada nombra un rol que ya existe y le escribe dos columnas.
 */
export interface GuardarDescripcionInput {
  roles: ({
    id: number;
    descripcion_deslindes: string | null;
    descripcion_deslindes_validada: boolean;
  } & ValoresLegalesDelRol)[];
  notas_observaciones: string | null;
  /** Semestre del avalúo; `null` lo vacía. */
  avaluo_fiscal_periodo: string | null;
}

/** Los datos legales de un rol tal como viajan al guardar: `null` es «sin dato». */
export interface ValoresLegalesDelRol {
  nombre_predio: string | null;
  superficie_titulos: number | null;
  superficie_sii: number | null;
  superficie_planimetria: number | null;
  destino_sii: string | null;
  avaluo_fiscal_clp: number | null;
  inscripcion_fojas: string | null;
  inscripcion_numero: string | null;
  inscripcion_anio: number | null;
  inscripcion_cbr: string | null;
}

export async function guardarDescripcionRequest(
  informeId: number,
  input: GuardarDescripcionInput,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/descripcion`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return respuesta.data;
}

/**
 * Sube la captura del KMZ con el límite de UN rol, reemplazando la que hubiera.
 *
 * `FormData` y no JSON, por lo mismo que la satelital: un archivo en un cuerpo
 * JSON habría que codificarlo en base64, lo que lo agranda un tercio y obliga a
 * leerlo entero en memoria de los dos lados.
 */
export async function subirImagenDelRolRequest(
  informeId: number,
  rolId: number,
  archivo: File,
): Promise<Informe> {
  const cuerpo = new FormData();
  cuerpo.append("imagen", archivo);

  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/roles/${rolId}/imagen`,
    { method: "POST", body: cuerpo },
  );
  return respuesta.data;
}

export async function quitarImagenDelRolRequest(
  informeId: number,
  rolId: number,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/roles/${rolId}/imagen`,
    { method: "DELETE" },
  );
  return respuesta.data;
}

/**
 * La URL para MOSTRAR la imagen del rol en un `<img src>`.
 *
 * El `hash` va de sufijo y NO es decoración: la respuesta se cachea una hora,
 * así que sin él el navegador seguiría mostrando la imagen anterior después de
 * un reemplazo. Como el hash cambia con el contenido, la URL cambia con él.
 */
export function urlDeLaImagenDelRol(informeId: number, rolId: number, hash: string): string {
  return urlDelApi(`/api/informes/${informeId}/roles/${rolId}/imagen?v=${hash}`);
}

export async function subirImagenDelExpedienteRequest(
  informeId: number,
  tipo: TipoDeImagenDelInforme,
  archivo: File,
): Promise<Informe> {
  const cuerpo = new FormData();
  cuerpo.append("imagen", archivo);

  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/imagenes/${tipo}`,
    { method: "POST", body: cuerpo },
  );
  return respuesta.data;
}

export async function quitarImagenDelExpedienteRequest(
  informeId: number,
  tipo: TipoDeImagenDelInforme,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/imagenes/${tipo}`,
    { method: "DELETE" },
  );
  return respuesta.data;
}

export function urlDeLaImagenDelExpediente(
  informeId: number,
  tipo: TipoDeImagenDelInforme,
  hash: string,
): string {
  return urlDelApi(`/api/informes/${informeId}/imagenes/${tipo}?v=${hash}`);
}
/* ---------------------------------------------------------------------------
   Las tres pestañas con CUADRO: maquinarias, instalaciones, construcciones.

   LAS SEIS LLAMADAS SON LAS MISMAS PARA LAS TRES, así que van parametrizadas
   por la pestaña y no copiadas. Eran ~130 líneas para maquinaria sola, de las
   cuales el 90 % era la misma plantilla `apiFetch<{data: Informe}>` con otra
   url; con dos pestañas más habrían sido cuatrocientas.

   TODAS DEVUELVEN EL INFORME ENTERO porque es lo que responde el endpoint: la
   pantalla siembra su cache con eso en vez de refrescar.
--------------------------------------------------------------------------- */

/** Las cuatro pestañas que tienen cuadro de filas con fotos. */
export type PestanaConCuadro =
  | "maquinarias"
  | "instalaciones"
  | "construcciones"
  | "plantaciones";

/**
 * Los dos tramos de url de cada una.
 *
 * SON DOS Y NO UNO por maquinaria: ahí la PESTAÑA es `/maquinaria` (singular)
 * y las FILAS son `/maquinarias`, mientras que en las otras dos el plural
 * español las hace coincidir y las distingue el verbo. Un solo segmento
 * obligaría a renombrar la ruta que ya está en producción.
 */
const RUTA_DE_LA_PESTANA: Record<PestanaConCuadro, { pestana: string; filas: string }> = {
  maquinarias: { pestana: "maquinaria", filas: "maquinarias" },
  instalaciones: { pestana: "instalaciones", filas: "instalaciones" },
  construcciones: { pestana: "construcciones", filas: "construcciones" },
  plantaciones: { pestana: "plantaciones", filas: "plantaciones" },
};

/**
 * El "Guardar cambios" de una pestaña con cuadro.
 *
 * NO LLEVA NINGUNA FILA: cada una tiene su propio botón y su propio endpoint,
 * porque tiene fotos colgando. Acá viaja lo que es de la PESTAÑA.
 */
export interface GuardarLaPestanaInput {
  no_aplica: boolean;
  descripcion_general: string | null;
  descripcion_general_validada: boolean;
  notas_observaciones: string | null;
}

/** El nombre con el que la pestaña de maquinaria ya llamaba a este cuerpo. */
export type GuardarMaquinariaInput = GuardarLaPestanaInput;

export async function guardarLaPestanaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  input: GuardarLaPestanaInput,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].pestana}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return respuesta.data;
}

/**
 * Los campos de UNA fila.
 *
 * En el alta van todos los que su formulario tiene; al corregir viaja SOLO lo
 * que cambió —la casilla "Validado" se manda sola— y por eso son opcionales.
 *
 * EL TIPO ES LAXO A PROPÓSITO: las tres pestañas tienen columnas distintas y
 * quien arma el cuerpo es el módulo de cada una
 * (`maquinaria.ts`, `instalaciones.ts`, `construcciones.ts`), que sí conoce
 * las suyas. Un tipo por pestaña acá obligaría a tres funciones iguales.
 */
export type CamposDeLaFila = Record<string, string | number | boolean | null | undefined>;

/** El nombre con el que la pestaña de maquinaria ya llamaba a este cuerpo. */
export type CamposDeMaquinaria = CamposDeLaFila;

export async function agregarFilaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  campos: CamposDeLaFila,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}`,
    { method: "POST", body: JSON.stringify(campos) },
  );
  return respuesta.data;
}

export async function corregirFilaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  filaId: number,
  campos: CamposDeLaFila,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}/${filaId}`,
    { method: "PATCH", body: JSON.stringify(campos) },
  );
  return respuesta.data;
}

/**
 * «Mantener» en el aviso de terreno «No está en el predio»: el servidor deja
 * escrito cuándo se decidió y devuelve la ficha entera. Solo maquinaria e
 * instalaciones tienen ese aviso.
 */
export async function mantenerFilaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  filaId: number,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}/${filaId}/mantener`,
    { method: "POST" },
  );
  return respuesta.data;
}

export async function quitarFilaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  filaId: number,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}/${filaId}`,
    { method: "DELETE" },
  );
  return respuesta.data;
}

/**
 * Agrega UNA foto a una fila.
 *
 * De a una aunque el botón diga "Adjuntar fotos" en plural: con seis en un
 * cuerpo, un rechazo obliga a decidir entre guardar las buenas o descartar
 * todo, y las dos respuestas son malas. De a una, cada foto tiene su propio
 * resultado y la pantalla puede decir cuál no entró.
 */
export async function subirFotoDeLaFilaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  filaId: number,
  archivo: File,
): Promise<Informe> {
  const cuerpo = new FormData();
  cuerpo.append("foto", archivo);

  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}/${filaId}/fotos`,
    { method: "POST", body: cuerpo },
  );
  return respuesta.data;
}

export async function quitarFotoDeLaFilaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  filaId: number,
  fotoId: number,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}/${filaId}/fotos/${fotoId}`,
    { method: "DELETE" },
  );
  return respuesta.data;
}

/**
 * La URL para MOSTRAR la miniatura de una foto en un `<img src>`.
 *
 * El `hash` va de sufijo por lo mismo que en las otras imágenes: la respuesta
 * se cachea una hora, y como el hash cambia con el contenido, la URL también.
 */
export function urlDeLaFotoDeLaFila(
  cuadro: PestanaConCuadro,
  informeId: number,
  filaId: number,
  fotoId: number,
  hash: string,
): string {
  return urlDelApi(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}/${filaId}/fotos/${fotoId}?v=${hash}`,
  );
}

/**
 * Adjunta un archivo recibido a un tipo de documento del informe.
 *
 * Cada llamada AGREGA uno: de un mismo documento pueden llegar el informe, sus
 * anexos y la planilla. No hay "reemplazar" — se adjunta el nuevo y se quita el
 * viejo, que son dos hechos.
 *
 * Devuelve el INFORME entero porque es lo que responde el endpoint: la pantalla
 * siembra su cache con eso en vez de refrescar.
 */
export async function adjuntarArchivoDeAntecedenteRequest(
  informeId: number,
  documentoTipoId: number,
  archivo: File,
): Promise<Informe> {
  const cuerpo = new FormData();
  cuerpo.append("archivo", archivo);

  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/documentos/${documentoTipoId}/archivos`,
    { method: "POST", body: cuerpo },
  );
  return respuesta.data;
}

export async function quitarArchivoDeAntecedenteRequest(
  informeId: number,
  archivoId: number,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/archivos/${archivoId}`,
    { method: "DELETE" },
  );
  return respuesta.data;
}

/**
 * "Reintentar": reinicia un archivo trabado o fallido y vuelve a despachar su
 * conversión (`odd/tasks/transcripcion-en-limbo.md`).
 *
 * Para CUALQUIER origen, equipo o cliente: al revés que quitar, esto no es
 * destructivo —no borra nada del expediente, solo vuelve a intentar producir
 * el Markdown que la IA lee— así que no hay motivo para distinguir `origen`
 * como sí hace la pantalla con el botón de quitar.
 *
 * El servidor responde 409 si el archivo sigue EN CURSO o si ya está `listo`
 * (`ArchivoDeAntecedenteController::reprocesar()`); la pantalla solo ofrece
 * este botón cuando `contexto_reintentable` es `true`, así que ese 409 es un
 * caso de carrera y no el camino esperado.
 *
 * Devuelve el INFORME entero, igual que sus vecinas: la pantalla siembra su
 * cache con eso en vez de refrescar.
 */
export async function reprocesarArchivoDeAntecedenteRequest(
  informeId: number,
  archivoId: number,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/archivos/${archivoId}/reprocesar`,
    { method: "POST" },
  );
  return respuesta.data;
}

/**
 * Clasifica un archivo en un tipo de documento, o lo devuelve a "sin
 * clasificar" con `null`.
 *
 * Sirve para tres cosas con el mismo verbo: clasificar lo que el cliente mandó
 * sin saber qué era, corregir una clasificación equivocada, y devolver un
 * archivo del cliente a la bandeja (solo si llegó por el portal; el servidor
 * responde 422 si no).
 *
 * Devuelve el INFORME entero, igual que sus vecinas: la pantalla siembra su
 * cache con eso en vez de refrescar.
 */
export async function clasificarArchivoDeAntecedenteRequest(
  informeId: number,
  archivoId: number,
  documentoTipoId: number | null,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/archivos/${archivoId}/clasificacion`,
    { method: "PATCH", body: JSON.stringify({ documento_tipo_id: documentoTipoId }) },
  );
  return respuesta.data;
}

/**
 * Corrige la fecha de EMISIÓN de un documento (rebanada 2 de
 * `odd/tasks/fecha-del-documento-y-tasacion-mas-reciente.md`).
 *
 * `fecha` en `null` VACÍA las dos columnas del lado del servidor
 * (`InformeDocumentoArchivo::corregirLaFechaDeEmision()`): no hay forma de
 * mandar "no toques nada" desde este endpoint, porque existe para una sola
 * cosa — escribir esta fecha.
 *
 * Devuelve el INFORME entero, igual que sus dos vecinas de arriba: la
 * pantalla siembra su cache con eso en vez de refrescar.
 */
export async function corregirFechaDelArchivoRequest(
  informeId: number,
  archivoId: number,
  fecha: string | null,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/archivos/${archivoId}/fecha`,
    { method: "PATCH", body: JSON.stringify({ fecha }) },
  );
  return respuesta.data;
}

/**
 * Escribe la nota del tasador sobre un archivo (`odd/tasks/notas-por-archivo.md`).
 *
 * `nota` en `null` la VACÍA; el servidor también vacía un texto en blanco.
 * Devuelve el INFORME entero, igual que {@see corregirFechaDelArchivoRequest}.
 */
export async function anotarElArchivoRequest(
  informeId: number,
  archivoId: number,
  nota: string | null,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/archivos/${archivoId}/nota`,
    { method: "PATCH", body: JSON.stringify({ nota }) },
  );
  return respuesta.data;
}

/**
 * La URL para DESCARGAR el archivo, para un `<a href>`.
 *
 * Va por `urlDelApi` y no por `apiFetch` por lo mismo que la imagen del bloque
 * y el PDF de la cotización: bajarlo con fetch obligaría a leer el binario a
 * memoria y envolverlo en un blob para terminar haciendo lo que el navegador ya
 * hace solo. La cookie de sesión se comparte entre `app.*` y `api.*`, así que el
 * enlace viaja autenticado.
 *
 * Sin sufijo de versión, al revés que la imagen: acá el id de la fila YA es
 * único por contenido —el mismo archivo dos veces en la misma categoría lo
 * rechaza el backend—, así que la URL nunca cambia de contenido y el caché de
 * una hora no puede servir algo viejo.
 */
export function urlDeArchivoDeAntecedente(informeId: number, archivoId: number): string {
  return urlDelApi(`/api/informes/${informeId}/archivos/${archivoId}`);
}

/**
 * Lo que la IA propone tras leer una tasación anterior.
 *
 * TODO PUEDE VENIR EN NULL, y no es defensa por si acaso: está medido. Una
 * cotización de Consultek trae los roles, la comuna y la superficie, y no trae
 * dirección ni descripciones — eso vive en el informe de tasación. La pantalla
 * tiene que saber mostrar "esto no estaba en el documento" como resultado
 * normal, no como error.
 */
export interface RolPropuesto {
  rol_sii: string;
  /** El CUT que resolvió el servidor, o `null` si no pudo. */
  comuna_codigo: string | null;
  /** El nombre del MAESTRO, nunca el que escribió el modelo. */
  comuna: string | null;
  superficie: number | null;
  superficie_construida: number | null;
}

export interface PropuestaDeTasacion {
  /* El cliente NO se propone (Rodgers, 08-10-2026): el del informe nuevo no es
     necesariamente el de la tasación anterior, así que la propuesta no trae
     empresa, persona de contacto ni correo. Los escribe la persona. */

  /* --- Qué se tasó --- */
  propiedad_nombre: string | null;
  direccion: string | null;
  superficie_ha: number | null;
  descripcion_ubicacion: string | null;
  descripcion: string | null;
  /**
   * En qué unidad están las superficies de la tabla de roles del documento.
   *
   * ES DEL CUADRO ENTERO Y NO DE CADA FILA, igual que en el informe: un total
   * no puede sumar 8.000 m² con 23 ha. Precargarla es la mitad de precargar
   * los roles — con la unidad equivocada, la superficie de cada fila queda
   * escrita bien y significando otra cosa.
   *
   * `null` cuando el documento no la declara, y ahí el formulario conserva la
   * que tenga. Mismo criterio que los dos enums de abajo: el servidor no
   * rellena con un default.
   */
  unidad_superficie: "ha" | "m2" | null;
  /** Esquema PLANO, como el resto del modelo: un rol es una fila. */
  roles: RolPropuesto[];
}

export interface ImportacionDeTasacion {
  ejecucion_id: number;
  propuesta: PropuestaDeTasacion;
}

/**
 * Lee el PDF de una tasación anterior y devuelve la propuesta.
 *
 * NO recibe el id del informe porque se llama ANTES de que exista: es para lo
 * que sirve. El archivo se retiene en el navegador y se adjunta después del
 * alta, con el id que ella devuelve.
 */
export async function importarTasacionRequest(archivo: File): Promise<ImportacionDeTasacion> {
  const cuerpo = new FormData();
  cuerpo.append("archivo", archivo);

  return apiFetch<ImportacionDeTasacion>("/api/ia/tasaciones", {
    method: "POST",
    body: cuerpo,
  });
}

/**
 * Registra si la persona usó o descartó la propuesta.
 *
 * Es lo único que mide si la función sirve, y no se puede reconstruir después:
 * una propuesta que nadie usó y una que se aceptó entera se ven idénticas en la
 * tabla si este dato no se escribe.
 */
export async function resolverImportacionRequest(
  ejecucionId: number,
  resultado: "aceptado" | "descartado",
): Promise<void> {
  // Sin `Content-Type` a mano: `apiFetch` lo pone en todo lo que no sea
  // `FormData`.
  await apiFetch(`/api/ia/tasaciones/${ejecucionId}`, {
    method: "PATCH",
    body: JSON.stringify({ resultado }),
  });
}

export async function actualizarInformeRequest(
  id: number,
  input: ActualizarInformeInput,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return respuesta.data;
}

/**
 * Guarda la definición COMPLETA de UNA pestaña de antecedentes (RF-E5), no un
 * delta: lo que no viaje en la lista vuelve al valor por defecto (Obligatorio) y su fila se borra
 * en el backend.
 *
 * `categoria` NO es opcional y no se deduce de los documentos: el borrado del
 * backend se acota con ella. Sin ese alcance, guardar los legales borraba los
 * históricos — y cuando la pestaña queda entera por defecto la lista puede viajar
 * vacía, así que no habría de dónde deducirla.
 */
export async function guardarDocumentosRequest(
  informeId: number,
  categoria: CategoriaDocumento,
  documentos: { documento_tipo_id: number; obligatoriedad: Obligatoriedad }[],
  notas: string | null,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${informeId}/documentos`, {
    method: "PUT",
    body: JSON.stringify({ categoria, documentos, notas_observaciones: notas }),
  });
  return respuesta.data;
}

/**
 * Genera la cotización y envía el informe a revisión (RF-F1 + RF-F3).
 *
 * Cada envío CREA una versión nueva y anula la anterior (§3.7), por eso es un
 * POST y no un PUT: no se reemplaza un recurso, se agrega otro a la cadena.
 */
/**
 * Descarga el PDF de la cotización vigente de un informe.
 *
 * El nombre del archivo lo pone el servidor (`Content-Disposition`), que es el
 * mismo que lleva el adjunto del correo al cliente. El de acá es solo el
 * respaldo por si esa cabecera no llega.
 */
export async function descargarPdfDeCotizacionRequest(informeId: number): Promise<void> {
  await descargarArchivo(
    `/api/informes/${informeId}/cotizacion/pdf`,
    `cotizacion-informe-${informeId}.pdf`,
  );
}

export async function crearCotizacionRequest(
  informeId: number,
  input: CrearCotizacionInput,
): Promise<Cotizacion> {
  const respuesta = await apiFetch<{ data: Cotizacion }>(
    `/api/informes/${informeId}/cotizaciones`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return respuesta.data;
}

/**
 * Registra a mano que el cliente aceptó la cotización.
 *
 * El motivo es OBLIGATORIO y lo valida el servidor, no solo el formulario: lo
 * que se guarda no es un dato sino el testimonio de una persona sobre algo que
 * pasó en otro canal, y ese testimonio hace nacer la fecha de entrega y le
 * dispara un correo al cliente.
 *
 * `cotizacion` en SINGULAR, igual que `aprobar`: lo que el cliente acepta es la
 * que está en pie, no una versión elegida por el llamador.
 */
export async function aceptarCotizacionRequest(
  informeId: number,
  motivo: string,
): Promise<Cotizacion> {
  const respuesta = await apiFetch<{ data: Cotizacion }>(
    `/api/informes/${informeId}/cotizacion/aceptar`,
    { method: "POST", body: JSON.stringify({ motivo }) },
  );
  return respuesta.data;
}

/**
 * Registra que el cliente pagó el abono.
 *
 * `fechaPago` es el día en que ENTRÓ la plata, no el de hoy: quien registra
 * suele enterarse después, y el servidor guarda las dos cosas por separado.
 *
 * Se manda en ISO `yyyy-mm-dd` y el servidor no acepta otra cosa: con un
 * formato libre, la hora se perdería en silencio contra una columna `date`.
 */
export async function registrarPagoDelAbonoRequest(
  informeId: number,
  fechaPago: string,
): Promise<Cotizacion> {
  const respuesta = await apiFetch<{ data: Cotizacion }>(
    `/api/informes/${informeId}/cotizacion/abono/pago`,
    { method: "POST", body: JSON.stringify({ fecha_pago: fechaPago }) },
  );
  return respuesta.data;
}

/**
 * Deshace el registro del pago: el abono vuelve a "pendiente".
 *
 * DELETE porque lo que se pide es borrar el registro, no agregar otro hecho
 * encima. Existe —y la aceptación no tiene equivalente— porque marcar el abono
 * pagado no le manda nada al cliente ni arranca ningún plazo: un error de
 * captura tiene que poder corregirse desde la pantalla.
 */
export async function revertirPagoDelAbonoRequest(informeId: number): Promise<Cotizacion> {
  const respuesta = await apiFetch<{ data: Cotizacion }>(
    `/api/informes/${informeId}/cotizacion/abono/pago`,
    { method: "DELETE" },
  );
  return respuesta.data;
}

/* --------------------------------------------------------------------------
   Pestaña "Descripción de suelos"
   -------------------------------------------------------------------------- */

/**
 * Lo que se guarda con el botón "Guardar cambios" de la pestaña.
 *
 * SOLO TEXTO: ni los encuadres ni las fotografías viajan acá. Cada imagen se
 * sube al soltarla, en su propia petición con su archivo, así que adjuntar una
 * no obliga a reenviar todo el texto de la pantalla ni al revés. Es la misma
 * división que "Descripción de la propiedad".
 *
 * EL CUADRO VA ENTERO Y NO FILA POR FILA: lo que el informe imprime es el
 * cuadro con su subtotal al pie, así que las filas se leen juntas y se guardan
 * juntas. Mandar solo lo que cambió dejaría filas nuevas al lado de viejas sin
 * que nadie pueda saber cuál es cuál mirándolo.
 */
export interface GuardarSuelosInput {
  /**
   * La prosa que se redacta SOBRE el cuadro de clases de uso.
   *
   * Se llama `descripcion_general` y no `descripcion_suelos` porque es la
   * clave del patrón que comparten todas las pestañas técnicas
   * (`informe_secciones.descripcion_general`): nombrarla por su pestaña haría
   * que cada una pidiera lo mismo con otro nombre.
   */
  descripcion_general: string | null;
  descripcion_general_validada: boolean;
  topografia: string | null;
  topografia_validada: boolean;
  descripcion_plano_regulador: string | null;
  /**
   * El cuadro, en el orden de la pantalla. El servidor usa la POSICIÓN como
   * `orden`: mandarlo como dato aparte dejaría dos fuentes para lo mismo.
   *
   * NUNCA viaja el código de la clase, solo su id: el código lo relee el
   * servidor del catálogo. Si viajara desde el navegador, cualquiera podría
   * congelar en un informe pericial una fila que dice "I" apuntando a la clase
   * "VIII", indistinguible de una legítima.
   */
  clases: {
    /** La fila ya guardada a la que corresponde; ausente si es nueva. */
    id?: number;
    suelo_clase_id: number;
    descripcion: string | null;
    superficie: string | null;
    validada: boolean;
  }[];
  notas_observaciones: string | null;
}

export async function guardarSuelosRequest(
  informeId: number,
  input: GuardarSuelosInput,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${informeId}/suelos`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return respuesta.data;
}

/* --- La pestaña "Descriptor de aguas" ------------------------------------- */

/**
 * De dónde sale el agua del predio.
 *
 * SON DOS Y NO HAY COMODÍN: el agua viene de la superficie o del subsuelo, y
 * no hay una tercera procedencia que anotar. Las OBRAS sí llevan "Otra".
 */
export type TipoDeFuenteDeAgua = "superficial_canal" | "subterranea_pozo";

export type TipoDeObraDeAgua = "tranque" | "embalse" | "obra_de_impulsion" | "otra";

export type EstadoDeInscripcionDelPozo = "inscrito" | "en_tramite" | "no_inscrito";

export const ETIQUETA_FUENTE_DE_AGUA: Record<TipoDeFuenteDeAgua, string> = {
  superficial_canal: "Agua superficial (canal)",
  subterranea_pozo: "Agua subterránea (pozo/noria)",
};

export const ETIQUETA_OBRA_DE_AGUA: Record<TipoDeObraDeAgua, string> = {
  tranque: "Tranque",
  embalse: "Embalse",
  obra_de_impulsion: "Obra de impulsión",
  otra: "Otra",
};

export const ETIQUETA_INSCRIPCION_DEL_POZO: Record<EstadoDeInscripcionDelPozo, string> = {
  inscrito: "Inscrito",
  en_tramite: "En trámite",
  no_inscrito: "No inscrito",
};

/**
 * Una fuente de agua ya guardada.
 *
 * LOS SEIS CAMPOS VIENEN SIEMPRE, incluidos los que el tipo no admite —que
 * llegan en `null` porque el servidor los descartó—. No se omiten: una clave
 * ausente obligaría al formulario a distinguir "no corresponde" de "no vino",
 * que acá son lo mismo.
 *
 * LOS DECIMALES LLEGAN COMO STRING (`decimal:N` de Eloquent) y así se usan: el
 * formulario los muestra y los vuelve a mandar, no los recalcula.
 */
export interface FuenteDeAguaDelInforme {
  id: number;
  tipo: TipoDeFuenteDeAgua;
  rio_canal: string | null;
  acciones: string | null;
  litros_seg: string | null;
  equivalencia_ls_accion: string | null;
  estado_inscripcion: EstadoDeInscripcionDelPozo | null;
  validada: boolean;
  /** El uuid de la fuente de terreno que esta fila reemplaza; `null` si se cargó a mano. */
  terreno_ref: string | null;
}

export interface ObraDeAguaDelInforme {
  id: number;
  tipo: TipoDeObraDeAgua;
  /**
   * PROSA y no un número con unidad: "Tranque con capacidad de 15.000 m³, uso
   * exclusivo de riego". Una obra de impulsión ni siquiera se mide en metros
   * cúbicos.
   */
  capacidad_descripcion: string | null;
  validada: boolean;
  /** El uuid de la obra de terreno que esta fila reemplaza; `null` si se cargó a mano. */
  terreno_ref: string | null;
}

/**
 * El "Guardar cambios" de la pestaña "Descriptor de aguas".
 *
 * LAS DOS LISTAS VIAJAN ACÁ, al revés que en las cuatro pestañas con cuadro y
 * fotos: de una fuente de agua no cuelga ningún objeto del bucket, así que se
 * reconcilian enteras como el cuadro de clases de uso de suelo.
 *
 * LLEVA `descripcion_general` desde el 08-10-2026, como las otras pestañas
 * técnicas (hasta el 13-09-2026 el servidor la rechazaba). La clave viaja
 * siempre: `null` es "sin texto".
 *
 * Las dos listas van en el ORDEN DE LA PANTALLA: el servidor usa la POSICIÓN
 * como `orden`, así que mandarlo aparte dejaría dos fuentes para lo mismo.
 */
export interface GuardarAguasInput {
  no_aplica: boolean;
  descripcion_general: string | null;
  descripcion_general_validada: boolean;
  fuentes: {
    /** Con id la fila se ACTUALIZA; sin id se CREA. Reconciliar por id es lo que conserva sus registros de terreno. */
    id?: number;
    /** Ausente conserva el enlace; `null` lo quita; un uuid lo crea. */
    terreno_ref?: string | null;
    tipo: TipoDeFuenteDeAgua;
    rio_canal: string | null;
    acciones: number | null;
    litros_seg: number | null;
    equivalencia_ls_accion: number | null;
    estado_inscripcion: EstadoDeInscripcionDelPozo | null;
    validada: boolean;
  }[];
  obras: {
    id?: number;
    terreno_ref?: string | null;
    tipo: TipoDeObraDeAgua;
    capacidad_descripcion: string | null;
    validada: boolean;
  }[];
  notas_observaciones: string | null;
}

export async function guardarAguasRequest(
  informeId: number,
  input: GuardarAguasInput,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(`/api/informes/${informeId}/agua`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return respuesta.data;
}

/**
 * AGREGA una fotografía a un encuadre. No reemplaza.
 *
 * Es la diferencia con `subirImagenDelExpedienteRequest`: de un mismo recorrido
 * salen diez fotos del terreno, que son diez hechos distintos y no diez
 * versiones de uno solo.
 *
 * La misma foto dos veces no falla ni duplica: el servidor la reconoce por su
 * hash y devuelve la ficha sin crear una segunda fila.
 */
export async function subirFotografiaRequest(
  informeId: number,
  tipo: TipoDeFotografiaDelInforme,
  archivo: File,
): Promise<Informe> {
  const cuerpo = new FormData();
  cuerpo.append("imagen", archivo);

  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/fotografias/${tipo}`,
    { method: "POST", body: cuerpo },
  );
  return respuesta.data;
}

/**
 * Quita UNA fotografía, por su id.
 *
 * Lleva id y la subida no: al subir todavía no hay fila a la que dirigirse y
 * el encuadre alcanza para decidir dónde va; al quitar hay que decir CUÁL de
 * las diez.
 */
export async function quitarFotografiaRequest(
  informeId: number,
  fotografiaId: number,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/fotografias/${fotografiaId}`,
    { method: "DELETE" },
  );
  return respuesta.data;
}

/**
 * Escribe la leyenda libre de una fotografía. `null` (o blanco) la vacía.
 *
 * Se guarda sola y por su cuenta, como la nota de un archivo: no es parte del
 * «Guardar cambios» de la pestaña.
 */
export async function rotularLaFotografiaRequest(
  informeId: number,
  fotografiaId: number,
  leyenda: string | null,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/fotografias/${fotografiaId}/leyenda`,
    { method: "PATCH", body: JSON.stringify({ leyenda }) },
  );
  return respuesta.data;
}

/** Escribe la leyenda libre de la foto de una fila (máquina, obra, construcción, plantación). */
export async function rotularLaFotoDeLaFilaRequest(
  cuadro: PestanaConCuadro,
  informeId: number,
  filaId: number,
  fotoId: number,
  leyenda: string | null,
): Promise<Informe> {
  const respuesta = await apiFetch<{ data: Informe }>(
    `/api/informes/${informeId}/${RUTA_DE_LA_PESTANA[cuadro].filas}/${filaId}/fotos/${fotoId}/leyenda`,
    { method: "PATCH", body: JSON.stringify({ leyenda }) },
  );
  return respuesta.data;
}

/**
 * La URL para MOSTRAR una fotografía en un `<img src>`.
 *
 * El `hash` va de sufijo por lo mismo que en las otras: la respuesta se cachea
 * una hora. Acá la fila no se reemplaza nunca —se agrega y se quita— así que
 * el cache-buster es menos crítico, y va igual para no tener dos formas de
 * armar la url de una imagen del expediente.
 */
export function urlDeLaFotografia(
  informeId: number,
  fotografiaId: number,
  hash: string,
): string {
  return urlDelApi(`/api/informes/${informeId}/fotografias/${fotografiaId}?v=${hash}`);
}
